import React, { useState, useRef, useEffect, useCallback } from 'react';
import { parseLearnignHTML, validateExtractedData } from '../utils/htmlDataExtractor';
import { injectSelectionHandler, cleanupElementSelector, extractElementData } from '../utils/elementSelector';
const InteractivePreview = ({ 
  onClose, 
  initialHtmlContent = null 
}) => {
  // 상태 관리
  const [htmlContent, setHtmlContent] = useState(initialHtmlContent || '');
  const [originalHtmlContent, setOriginalHtmlContent] = useState(initialHtmlContent || ''); // 원본 HTML 보관
  const [editableHTML, setEditableHTML] = useState(''); // AI편집용 HTML
  const [iframeKey, setIframeKey] = useState(0);
  // 원본 AI편집 템플릿을 상수로 보관 (리렌더링에 영향받지 않음)
  const originalEditableHTMLRef = useRef('');
  const [extractedData, setExtractedData] = useState(null);
  const [originalExtractedData, setOriginalExtractedData] = useState(null); // 원본 추출 데이터 보관
  const [wordsList, setWordsList] = useState([]); // 단어 목록을 별도 state로 관리
  const [selectedElement, setSelectedElement] = useState(null);
  const [showEditPanel, setShowEditPanel] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [history, setHistory] = useState([]);
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(-1);
  
  // AI 채팅으로 변경된 디자인 설정 저장 (다운로드 시 사용)
  const [aiDesignSettings, setAiDesignSettings] = useState(null);
  
  // 디자인 설정 상태 (부모에서 관리)
  const [designSettings, setDesignSettings] = useState({
    background: { start: '#667eea', end: '#764ba2' },
    card: { start: '#4facfe', end: '#00f2fe' },
    font: { primary: '#ffffff', secondary: '#ffffff' }
  });

  // HTML에서 CSS 변수 추출하는 함수
  const extractDesignSettingsFromHTML = useCallback((htmlContent) => {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlContent, 'text/html');
      
      // CSS 변수 추출을 위한 정규식 패턴
      const cssVariablePatterns = {
        bgStart: /--bg-start:\s*([#\w\s,()]+);/,
        bgEnd: /--bg-end:\s*([#\w\s,()]+);/,
        cardStart: /--card-start:\s*([#\w\s,()]+);/,
        cardEnd: /--card-end:\s*([#\w\s,()]+);/
      };
      
      const extractedSettings = {
        background: { start: '#667eea', end: '#764ba2' },
        card: { start: '#4facfe', end: '#00f2fe' },
        font: { primary: '#ffffff', secondary: '#ffffff' }
      };
      
      // HTML에서 CSS 내용 찾기
      const styleElements = doc.querySelectorAll('style');
      let cssContent = '';
      styleElements.forEach(style => {
        cssContent += style.textContent || '';
      });
      
      // CSS 변수 값 추출
      const bgStartMatch = cssContent.match(cssVariablePatterns.bgStart);
      const bgEndMatch = cssContent.match(cssVariablePatterns.bgEnd);
      const cardStartMatch = cssContent.match(cssVariablePatterns.cardStart);
      const cardEndMatch = cssContent.match(cssVariablePatterns.cardEnd);
      
      if (bgStartMatch) extractedSettings.background.start = bgStartMatch[1].trim();
      if (bgEndMatch) extractedSettings.background.end = bgEndMatch[1].trim();
      if (cardStartMatch) extractedSettings.card.start = cardStartMatch[1].trim();
      if (cardEndMatch) extractedSettings.card.end = cardEndMatch[1].trim();
      
      console.log('🎨 추출된 디자인 설정:', extractedSettings);
      return extractedSettings;
      
    } catch (error) {
      console.warn('⚠️ CSS 변수 추출 실패:', error);
      return {
        background: { start: '#667eea', end: '#764ba2' },
        card: { start: '#4facfe', end: '#00f2fe' },
        font: { primary: '#fff', secondary: '#666666' }
      };
    }
  }, []);

  // 단어 수정 의도 확인 관련 상태
  const [modificationMode, setModificationMode] = useState('preserve'); // 'preserve' or 'modify'
  
  // refs
  const editIframeRef = useRef(null); // AI편집용 iframe
  const fileInputRef = useRef(null);
  
  // HTML 콘텐츠 처리 (먼저 정의)
  const processHTMLContent = useCallback(async (content) => {
    try {
      setIsLoading(true);
      
      // 데이터 추출
      const extracted = parseLearnignHTML(content);
      const validation = validateExtractedData(extracted);
      
      if (!validation.isValid) {
        setError(`데이터 추출 문제: ${validation.issues.join(', ')}`);
        return;
      }
      // 원본 데이터 저장
      setOriginalHtmlContent(content);
      setOriginalExtractedData(extracted);
      
      // 상태 업데이트
      setExtractedData(extracted);
      setWordsList(extracted.vocabularyData || []);
      setHtmlContent(content);
      
      // HTML에서 디자인 설정 추출 및 초기화
      const extractedDesignSettings = extractDesignSettingsFromHTML(content);
      setDesignSettings(extractedDesignSettings);
      
      // AI편집 모드 활성화 - AI편집 템플릿 로드
      await loadAIEditingTemplate(extracted.templateType, extracted);
      
      // 히스토리 초기화
      setHistory([content]);
      setCurrentHistoryIndex(0);
      
      setError('');
      
    } catch (error) {
      console.error('HTML 처리 실패:', error);
      setError('HTML 처리 중 오류가 발생했습니다: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  }, [originalEditableHTMLRef]);

  // HTML 파일 업로드 처리
  const handleFileUpload = useCallback(async (event) => {
    const file = event.target.files[0];
    if (!file) {

      return;
    }
    if (!file.name.endsWith('.html') && !file.name.endsWith('.htm')) {
      setError('HTML 파일만 업로드할 수 있습니다.');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const content = await file.text();

      await processHTMLContent(content);
    } catch (error) {
      console.error('파일 읽기 실패:', error);
      setError('파일을 읽을 수 없습니다: ' + error.message);
    } finally {
      setIsLoading(false);
      // 파일 입력 초기화
      event.target.value = '';
    }
  }, [processHTMLContent]);

  // AI편집 템플릿 로드
  const loadAIEditingTemplate = useCallback(async (templateType, data) => {
    try {

      
      // AI편집 템플릿 파일 매핑
      const templateFiles = {
        flashcard: 'flashcard-editor.html',
        quiz: 'multiple-choice-quiz-editor.html',
        'multiple-choice': 'multiple-choice-quiz-editor.html',
        'short-answer': 'short-answer-quiz-editor.html',
        passage: 'passage-worksheet-editor.html',
        questions: 'question-generation-editor.html',
        shadowing: 'shadowing-editor.html',
        essay: 'essay-feedback-editor.html'
      };
      
      const templateFile = templateFiles[templateType] || 'flashcard-editor.html';
      const templatePath = `/templates/editor/${templateFile}`;
      
      // AI편집 템플릿 로드

      const response = await fetch(templatePath);
      if (!response.ok) {
        throw new Error(`AI편집 템플릿을 찾을 수 없습니다: ${templatePath}`);
      }
      
      let editorHTML = await response.text();


      
      // 추출된 데이터를 AI편집 템플릿에 주입
      editorHTML = injectDataToEditorTemplate(editorHTML, data);
      


      
      setEditableHTML(editorHTML);
      
      // 최초 로드 시에만 원본 AI편집 템플릿을 상수로 저장 (되돌리기용)
      if (!originalEditableHTMLRef.current) {
        originalEditableHTMLRef.current = editorHTML;

      }
      

      
    } catch (error) {
      console.error('AI편집 템플릿 로드 실패:', error);
      setError('AI편집 템플릿 로드 실패: ' + error.message);
    }
  }, []);

  // 데이터를 AI편집 템플릿에 주입
  const injectDataToEditorTemplate = useCallback((editorHTML, data) => {
    try {


      
      let updatedHTML = editorHTML;
      
      // 1. 데이터 구조 통일 (플래시카드 편집 템플릿용)
      const safeData = JSON.parse(JSON.stringify(data)); // 깊은 복사로 안전한 데이터 생성
      
      // 플래시카드 템플릿에 맞게 데이터 구조 변환
      if (safeData.vocabularyData && Array.isArray(safeData.vocabularyData)) {
        safeData.words = safeData.vocabularyData.map(item => ({
          word: item.word || item.english || '',
          meaning: item.meaning || item.korean || '',
          example: item.example || item.sentence || ''
        }));

      }
      
      // templateType 설정
      safeData.templateType = 'flashcard';
      safeData.title = '단어 플래시 카드';
      
      // JSON 내 특수문자 정리
      const cleanData = (obj) => {
        if (typeof obj === 'string') {
          return obj.replace(/[\u0000-\u001F\u007F-\u009F]/g, ''); // 제어문자 제거
        }
        if (Array.isArray(obj)) {
          return obj.map(cleanData);
        }
        if (obj && typeof obj === 'object') {
          const result = {};
          for (const key in obj) {
            result[key] = cleanData(obj[key]);
          }
          return result;
        }
        return obj;
      };
      
      const cleanedData = cleanData(safeData);
      
      // LearningLogger 코드 제거하고 순수 데이터만 남김
      const pureData = {
        templateType: cleanedData.templateType,
        title: cleanedData.title,
        words: cleanedData.words,
        questions: cleanedData.questions,
        passageData: cleanedData.passageData,
        audioData: cleanedData.audioData,
        settingsData: cleanedData.settingsData
      };
      
      const dataScript = `<script type="application/json" id="template-data">
${JSON.stringify(pureData, null, 4)}
</script>`;
      
      // 정확한 template-data 스크립트 교체
      const beforeReplace = updatedHTML.length;
      const templateDataRegex = /<script type="application\/json" id="template-data">[\s\S]*?<\/script>/;
      const match = updatedHTML.match(templateDataRegex);
      
      if (match) {


      }
      
      updatedHTML = updatedHTML.replace(templateDataRegex, dataScript);
      
      const afterReplace = updatedHTML.length;
      console.log('✅ 템플릿 데이터 주입 완료:', {
        words: cleanedData.words?.length || 0,
        templateType: cleanedData.templateType,
        lengthChange: afterReplace - beforeReplace
      });
      
      // 교체 후 template-data 확인
      const newMatch = updatedHTML.match(templateDataRegex);
      if (newMatch) {


      }
      
      // 2. 실제 단어 카드 HTML 생성 및 교체 (플래시카드의 경우)
      if (cleanedData.words && Array.isArray(cleanedData.words)) {


        
        // 단어 카드 HTML 생성 (HTML 특수문자 이스케이프)
        const escapeHTML = (text) => {
          if (typeof text !== 'string') return text;
          return text
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
        };
        
        const wordCardsHTML = cleanedData.words.map((word, index) => `
                    <div class="word-card" data-edit-type="vocabulary" data-word-id="${index + 1}" data-index="${index}" draggable="true">
                        <div class="drag-handle" title="드래그해서 순서 변경">⋮⋮</div>
                        <span class="word editable" data-field="word">${escapeHTML(word.word || '단어')}</span>
                        <span class="meaning editable" data-field="meaning">${escapeHTML(word.meaning || '뜻')}</span>
                        <span class="example editable" data-field="example">${escapeHTML(word.example || '예문')}</span>
                    </div>`).join('');
        
        // 기존 샘플 단어들을 실제 데이터로 교체
        updatedHTML = updatedHTML.replace(
          /<!-- 샘플 단어들 -->[\s\S]*?(?=<button class="add-word-btn")/,
          `<!-- 실제 단어 데이터 -->${wordCardsHTML}
                    `
        );
        
        // 단어 수 업데이트
        updatedHTML = updatedHTML.replace(
          /단어 수: <span id="wordCount">\d+<\/span>개/,
          `단어 수: <span id="wordCount">${cleanedData.words.length}</span>개`
        );
        

      }
      
      // 3. 기타 템플릿 유형별 데이터 처리
      if (data.questions && Array.isArray(data.questions)) {

        // 문제 HTML 생성 로직 (필요시 구현)
      }
      


      
      // 최종 확인: template-data 스크립트만 추출해서 검사
      const finalTemplateMatch = updatedHTML.match(/<script type="application\/json" id="template-data">[\s\S]*?<\/script>/);
      if (finalTemplateMatch) {


        
        if (finalTemplateMatch[0].includes('LearningLogger')) {
          console.error('❌ template-data 스크립트에 LearningLogger가 포함됨!');
        }
      }
      
      // 드래그 앤 드롭 기능만 유지
      const editingFeatures = `
        <script>
        // 드래그 앤 드롭 기능 개선
        function initializeDragDrop() {
          const container = document.querySelector('.word-cards-container');
          if (!container) return;
          
          let draggedElement = null;
          let draggedIndex = -1;
          
          container.addEventListener('dragstart', function(e) {
            if (e.target.classList.contains('word-card') || e.target.closest('.word-card')) {
              draggedElement = e.target.classList.contains('word-card') ? e.target : e.target.closest('.word-card');
              draggedIndex = parseInt(draggedElement.dataset.index);
              draggedElement.style.opacity = '0.5';
              draggedElement.classList.add('dragging');

            }
          });
          
          container.addEventListener('dragend', function(e) {
            if (draggedElement) {
              draggedElement.style.opacity = '';
              draggedElement.classList.remove('dragging');
            }
          });
          
          container.addEventListener('dragover', function(e) {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
          });
          
          container.addEventListener('drop', function(e) {
            e.preventDefault();
            const dropTarget = e.target.closest('.word-card');
            
            if (dropTarget && draggedElement && dropTarget !== draggedElement) {
              const dropIndex = parseInt(dropTarget.dataset.index);

              
              // DOM에서 요소 이동
              if (draggedIndex < dropIndex) {
                container.insertBefore(draggedElement, dropTarget.nextSibling);
              } else {
                container.insertBefore(draggedElement, dropTarget);
              }
              
              // 인덱스 재정렬
              const wordCards = container.querySelectorAll('.word-card');
              wordCards.forEach((card, newIndex) => {
                card.dataset.index = newIndex;
                card.dataset.wordId = newIndex + 1;
              });
              

              
              // 변경사항을 상위 컴포넌트에 알림
              const currentWords = Array.from(wordCards).map(card => ({
                word: card.querySelector('.word, .word-input')?.textContent || card.querySelector('.word, .word-input')?.value || '',
                meaning: card.querySelector('.meaning, .meaning-input')?.textContent || card.querySelector('.meaning, .meaning-input')?.value || '',
                example: card.querySelector('.example, .example-input')?.textContent || card.querySelector('.example, .example-input')?.value || ''
              }));
              
              if (window.parent && window.parent.postMessage) {
                window.parent.postMessage({
                  type: 'WORDS_REORDERED',
                  data: currentWords
                }, '*');
              }
            }
          });
          

        }
        
        // 페이지 로드 시 드래그 앤 드롭 초기화
        function initializeFeatures() {

          initializeDragDrop();
        }
        
        // 페이지 로드 시 초기화
        document.addEventListener('DOMContentLoaded', initializeFeatures);
        setTimeout(initializeFeatures, 1000);
        </script>
        
        <style>
        
        .word-card.dragging {
          opacity: 0.5;
          transform: rotate(2deg);
        }
        
        .drag-handle {
          cursor: grab;
          color: #9ca3af;
          padding: 4px;
          font-size: 16px;
          user-select: none;
        }
        
        .drag-handle:hover {
          color: #3b82f6;
        }
        
        .drag-handle:active {
          cursor: grabbing;
        }
        
        </style>
      `;
      
      // 편집 기능을 HTML에 추가 (</body> 태그 전에)
      if (updatedHTML.includes('</body>')) {
        updatedHTML = updatedHTML.replace('</body>', editingFeatures + '</body>');
      } else {
        // </body> 태그가 없으면 맨 끝에 추가
        updatedHTML = updatedHTML + editingFeatures;
      }
      

      
      return updatedHTML;
      
    } catch (error) {
      console.error('❌ 데이터 주입 실패:', error);
      return editorHTML;
    }
  }, []);
  
  // AI편집 iframe 로드 완료 처리
  const handleEditIframeLoad = useCallback(() => {
    if (!editIframeRef.current || !editableHTML) return;
    
    try {
      // sheetId 자동 주입 (기존 HTML 파일에 sheetId 추가)
      const iframeDoc = editIframeRef.current.contentDocument;
      if (iframeDoc) {
        const scripts = iframeDoc.querySelectorAll('script');
        let learningLoggerScript = null;
        
        // LearningLogger 클래스가 있는 스크립트 찾기
        for (const script of scripts) {
          if (script.textContent && script.textContent.includes('class LearningLogger')) {
            learningLoggerScript = script;
            break;
          }
        }
        
        if (learningLoggerScript) {
          let scriptContent = learningLoggerScript.textContent;
          
          // 이미 sheetId가 있는지 확인
          if (!scriptContent.includes('sheetId')) {
            // webAppUrl 라인 다음에 sheetId 추가
            const webAppUrlRegex = /(this\.webAppUrl\s*=\s*['"](.*?)['"];?)/;
            const match = scriptContent.match(webAppUrlRegex);
            
            if (match) {
              const sheetIdLine = `\n                this.sheetId = '1rHdDtqzPgxWaKa2eiGdgHFJtPjsGO4V8rr37W8kXiog';`;
              scriptContent = scriptContent.replace(match[1], match[1] + sheetIdLine);
            }
            
            // sendToGoogleSheet 함수에서 localStorage의 sheetId 사용하고 순서 보장하도록 수정
            if (!scriptContent.includes('form.append(\'sheetId\'')) {
              scriptContent = scriptContent.replace(
                /Object\.entries\(logData\)\.forEach\(\(\[key, value\]\) => \{\s*form\.append\(key, value\);\s*\}\);/,
                `const savedSheetId = localStorage.getItem('selectedSpreadsheetId') || '1rHdDtqzPgxWaKa2eiGdgHFJtPjsGO4V8rr37W8kXiog';
                    form.append('sheetId', savedSheetId);
                    
                    // 구글 시트 컬럼 순서에 맞게 데이터 전송
                    const orderedFields = ['기능명', '학생이름', '접속시간', '학습시간', '정오답정보', '힌트사용여부', '부가정보', '기록시간'];
                    orderedFields.forEach(field => {
                        if (logData[field] !== undefined) {
                            form.append(field, logData[field]);
                        }
                    });`
              );
            }
            
            learningLoggerScript.textContent = scriptContent;

          } else {

          }
        } else {

        }
      }
      
      // 기본 선택 기능 활성화
      injectSelectionHandler(editIframeRef.current, handleElementSelect);

    } catch (error) {
      console.error('AI편집 iframe 활성화 실패:', error);
    }
  }, [editableHTML]);

  // iframe에서 보내는 메시지 처리 (단어 업데이트/재정렬)
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data.type === 'WORDS_UPDATED') {

        
        // 단어 목록 상태 업데이트 (데이터 구조 통일)
        const normalizedWords = event.data.data.map(item => ({
          word: item.word || item.english || '',
          meaning: item.meaning || item.korean || '',
          example: item.example || item.sentence || ''
        }));
        setWordsList(normalizedWords);
        
        // extractedData 업데이트
        const newExtractedData = {
          ...extractedData,
          vocabularyData: event.data.data,
          words: event.data.data
        };
        setExtractedData(newExtractedData);
        
      } else if (event.data.type === 'WORDS_REORDERED') {

        
        // 단어 목록 상태 업데이트 (데이터 구조 통일)
        const normalizedWords = event.data.data.map(item => ({
          word: item.word || item.english || '',
          meaning: item.meaning || item.korean || '',
          example: item.example || item.sentence || ''
        }));
        setWordsList(normalizedWords);
        
        // extractedData 업데이트
        const newExtractedData = {
          ...extractedData,
          vocabularyData: event.data.data,
          words: event.data.data
        };
        setExtractedData(newExtractedData);
      }
    };

    window.addEventListener('message', handleMessage);
    
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [extractedData]);

  
  // 요소 선택 처리
  const handleElementSelect = useCallback((elementInfo) => {

    
    // 특정 UI 요소들은 선택 무시
    
    const elementData = extractElementData(elementInfo);
    const elementWithData = {
      ...elementInfo,
      data: elementData,
      wordData: elementData // 단어 데이터 추가
    };
    
    setSelectedElement(elementWithData);
    
    // 개별 단어 카드가 선택된 경우에도 편집 패널 표시 (채팅창에서 의도 확인)
    setShowEditPanel(true);
  }, []);
  
  // 편집 패널 닫기
  const handleCloseEditPanel = useCallback(() => {
    setShowEditPanel(false);
    setSelectedElement(null);
  }, []);

  // 단어 수정 모드 변경 핸들러
  const handleModificationModeChange = useCallback((mode) => {
    setModificationMode(mode);
  }, []);
  
  // iframe 내 CSS 변수 실시간 업데이트 
  const updateIframeCSSVariables = useCallback((cssUpdates) => {
    try {
      const iframe = editIframeRef.current;
      if (!iframe) {
        console.warn('iframe을 찾을 수 없습니다.');
        return;
      }
      
      const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
      if (!iframeDoc) {
        console.warn('iframe 문서에 접근할 수 없습니다.');
        return;
      }
      
      // CSS 변수 업데이트
      Object.entries(cssUpdates).forEach(([property, value]) => {
        iframeDoc.documentElement.style.setProperty(property, value);
        console.log(`CSS 변수 업데이트: ${property} = ${value}`);
      });
      
      console.log('✅ CSS 변수 실시간 업데이트 완료');
    } catch (error) {
      console.error('❌ CSS 변수 업데이트 실패:', error);
    }
  }, []);
  
  // iframe 내 단어 데이터 실시간 업데이트
  const updateIframeVocabularyData = useCallback((newWords) => {
    try {
      const iframe = editIframeRef.current;
      if (!iframe) {
        console.warn('iframe을 찾을 수 없습니다.');
        return;
      }
      
      const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
      if (!iframeDoc) {
        console.warn('iframe 문서에 접근할 수 없습니다.');
        return;
      }
      
      // 단어 카드 컨테이너 찾기
      const wordGrid = iframeDoc.querySelector('#wordGrid, .word-grid, .word-cards-container');
      if (!wordGrid) {
        console.warn('단어 그리드를 찾을 수 없습니다.');
        return;
      }
      
      // 기존 단어 카드들 제거 (추가 버튼 제외)
      const existingCards = wordGrid.querySelectorAll('.word-card:not(.add-word-btn)');
      existingCards.forEach(card => card.remove());
      
      // 새로운 단어 카드들 추가  
      newWords.forEach((word, index) => {
        const cardHTML = `
          <div class="word-card" data-word-id="${index + 1}" draggable="true">
            <div class="drag-handle">⋮⋮</div>
            <span class="word editable" data-field="word">${word.word || '단어'}</span>
            <span class="meaning editable" data-field="meaning">${word.meaning || '의미'}</span>
            <span class="example editable" data-field="example">${word.example || '예문'}</span>
          </div>
        `;
        wordGrid.insertAdjacentHTML('beforeend', cardHTML);
      });
      
      console.log('✅ 단어 데이터 실시간 업데이트 완료');
    } catch (error) {
      console.error('❌ 단어 데이터 업데이트 실패:', error);
    }
  }, []);
  
  // 드래그 앤 드롭 처리
  const handleDrop = useCallback(async (event) => {
    event.preventDefault();
    const files = Array.from(event.dataTransfer.files);
    const htmlFile = files.find(file => 
      file.name.endsWith('.html') || file.name.endsWith('.htm')
    );
    
    if (htmlFile) {
      const content = await htmlFile.text();
      await processHTMLContent(content);
    } else {
      setError('HTML 파일을 드래그해주세요.');
    }
  }, [processHTMLContent]);
  
  const handleDragOver = useCallback((event) => {
    event.preventDefault();
  }, []);
  

  // 템플릿 렌더링 함수 - {{}} placeholder 방식
  const renderTemplate = useCallback((templateHTML, data) => {
    console.log('🔧 renderTemplate 호출');
    console.log('📋 전달받은 data:', data);
    console.log('🎨 data.COLORS:', data?.COLORS);
    
    if (!data) {
      console.warn('⚠️ renderTemplate: data가 undefined입니다');
      return templateHTML;
    }
    
    let renderedHTML = templateHTML;
    
    // HTML에서 CSS 변수 부분 찾기
    const bgStartLine = templateHTML.match(/--bg-start:[^;]*;[^}]*/);
    console.log('🔍 현재 --bg-start 라인:', bgStartLine ? bgStartLine[0] : 'not found');
    
    const colors = data.COLORS || {};
    console.log('🎨 교체할 색상들:', colors);
    
    // 더 간단한 패턴으로 교체 (주석 포함)
    renderedHTML = renderedHTML.replace(
      /--bg-start:\s*[^;]*;\s*\/\*[^*]*\*\//g, 
      `--bg-start: ${colors.BACKGROUND_START || '#667eea'};`
    );
    renderedHTML = renderedHTML.replace(
      /--bg-end:\s*[^;]*;\s*\/\*[^*]*\*\//g, 
      `--bg-end: ${colors.BACKGROUND_END || '#764ba2'};`
    );
    renderedHTML = renderedHTML.replace(
      /--card-start:\s*[^;]*;\s*\/\*[^*]*\*\//g, 
      `--card-start: ${colors.CARD_START || '#4facfe'};`
    );
    renderedHTML = renderedHTML.replace(
      /--card-end:\s*[^;]*;\s*\/\*[^*]*\*\//g, 
      `--card-end: ${colors.CARD_END || '#00f2fe'};`
    );
        renderedHTML = renderedHTML.replace(
      /--card-font:\s*[^;]*;\s*\/\*[^*]*\*\//g, 
      `--card-font: ${colors.FONT || '#FFFFFF'};`
    );
    
    // 단순 placeholder 교체: {{KEY}} → value
    renderedHTML = renderedHTML.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, key) => {
      return data[key] !== undefined ? data[key] : match;
    });
    
    // 중첩 객체 지원: {{COLORS.BACKGROUND}} → value
    renderedHTML = renderedHTML.replace(/\{\{\s*(\w+)\.(\w+)\s*\}\}/g, (match, obj, key) => {
      return data[obj] && data[obj][key] !== undefined ? data[obj][key] : match;
    });
    
    // 깊은 중첩 지원: {{COLORS.BACKGROUND.START}} → value  
    renderedHTML = renderedHTML.replace(/\{\{\s*(\w+)\.(\w+)\.(\w+)\s*\}\}/g, (match, obj, subObj, key) => {
      return data[obj] && data[obj][subObj] && data[obj][subObj][key] !== undefined 
        ? data[obj][subObj][key] 
        : match;
    });
    
    // AI 색상 변경 메시지 핸들러 스크립트 추가
    const messageHandlerScript = `
    <script>
      // AI 색상 변경 메시지 핸들러
      window.addEventListener('message', function(event) {
        if (event.data.type === 'CHANGE_COLOR') {
          const { colorType, colorValue } = event.data.data;

          
          if (!colorValue) {
            console.warn('⚠️ 색상 값이 없습니다:', colorType);
            return;
          }
          
          const body = document.body;
          const cards = document.querySelectorAll('.card, .flashcard, .word-card');
          const buttons = document.querySelectorAll('.btn, .button, .start-btn, .retry-btn');
          const progressBars = document.querySelectorAll('.progress-bar');
          
          switch(colorType) {
            case 'backgroundStart':
            case 'backgroundEnd':
              // 배경 그라데이션 업데이트
              const currentBg = body.style.background || '';
              const colors = currentBg.match(/#[a-fA-F0-9]{6}/g) || ['#667eea', '#764ba2'];
              if (colorType === 'backgroundStart') colors[0] = colorValue;
              if (colorType === 'backgroundEnd') colors[1] = colorValue;
              body.style.background = \`linear-gradient(135deg, \${colors[0]} 0%, \${colors[1]} 100%)\`;
              
              // 버튼 색상도 함께 업데이트
              buttons.forEach(btn => {
                btn.style.background = \`linear-gradient(135deg, \${colors[0]} 0%, \${colors[1]} 100%)\`;
              });
              break;
              
            case 'cardStart':
            case 'cardEnd':
              // 카드 그라데이션 업데이트
              cards.forEach(card => {
                const currentCardBg = card.style.background || '';
                const cardColors = currentCardBg.match(/#[a-fA-F0-9]{6}/g) || ['#4facfe', '#00f2fe'];
                if (colorType === 'cardStart') cardColors[0] = colorValue;
                if (colorType === 'cardEnd') cardColors[1] = colorValue;
                card.style.background = \`linear-gradient(135deg, \${cardColors[0]} 0%, \${cardColors[1]} 100%)\`;
              });
              
              // 진행바 색상도 함께 업데이트
              progressBars.forEach(bar => {
                const cardColors = cards.length > 0 
                  ? (cards[0].style.background || '').match(/#[a-fA-F0-9]{6}/g) || ['#4facfe', '#00f2fe']
                  : ['#4facfe', '#00f2fe'];
                bar.style.background = \`linear-gradient(90deg, \${cardColors[0]} 0%, \${cardColors[1]} 100%)\`;
              });
              break;
              
            case 'fontPrimary':
              // 주요 텍스트 색상
              const primaryElements = document.querySelectorAll('.word, .question, .primary-text, h1, h2, h3');
              primaryElements.forEach(el => el.style.color = colorValue);
              break;
              
            case 'fontSecondary':
              // 보조 텍스트 색상
              const secondaryElements = document.querySelectorAll('.meaning, .answer, .secondary-text, p, .description');
              secondaryElements.forEach(el => el.style.color = colorValue);
              break;
          }
        }
      });
    </script>
    `;
    
    // 메시지 핸들러를 </body> 태그 직전에 삽입
    if (renderedHTML.includes('</body>')) {
      renderedHTML = renderedHTML.replace('</body>', messageHandlerScript + '</body>');
    } else if (renderedHTML.includes('</html>')) {
      // body 태그가 없는 경우 html 종료 직전에 삽입
      renderedHTML = renderedHTML.replace('</html>', messageHandlerScript + '</html>');
    } else {
      // 완전한 HTML 구조가 없는 경우 끝에 추가
      renderedHTML += messageHandlerScript;
    }
    

    return renderedHTML;
  }, []);

  // AI 편집 데이터를 템플릿 변수로 변환
  const prepareTemplateData = useCallback((editedData, currentDesignSettings = {}) => {
    // 단어 데이터 처리
    const words = editedData.words || wordsList || [];
    const normalizedWords = words.map(word => ({
      english: word.word || word.english || '',
      korean: word.meaning || word.korean || '',
      example: word.example || word.sentence || ''
    }));
    
    // 디자인 설정 통합 (안전한 기본값 처리)
    const defaultColors = {
      background: { start: '#667eea', end: '#764ba2' },
      card: { start: '#4facfe', end: '#00f2fe' },
      font: { primary: '#333333', secondary: '#666666' }
    };
    
    const colors = {
      background: {
        start: currentDesignSettings?.background?.start || defaultColors.background.start,
        end: currentDesignSettings?.background?.end || defaultColors.background.end
      },
      card: {
        start: currentDesignSettings?.card?.start || defaultColors.card.start,
        end: currentDesignSettings?.card?.end || defaultColors.card.end
      },
      font: {
        primary: currentDesignSettings?.font?.primary || defaultColors.font.primary,
        secondary: currentDesignSettings?.font?.secondary || defaultColors.font.secondary
      }
    };
    
    // 레이아웃 설정
    const layout = editedData.layout || {
      cardWidth: 300,
      cardHeight: 200,
      containerPadding: 40,
      fontSize: 18
    };
    
    const templateData = {
      // 단어 데이터
      WORD_DATA: JSON.stringify(normalizedWords, null, 2),
      TOTAL_WORDS: normalizedWords.length,
      
      // 색상 설정 (CSS 문자열)
      BACKGROUND_GRADIENT: `linear-gradient(135deg, ${colors.background.start}, ${colors.background.end})`,
      CARD_GRADIENT: `linear-gradient(135deg, ${colors.card.start}, ${colors.card.end})`,
      FONT_PRIMARY_COLOR: colors.font.primary,
      FONT_SECONDARY_COLOR: colors.font.secondary,
      
      // 레이아웃 설정 (픽셀 값)
      CARD_WIDTH: layout.cardWidth,
      CARD_HEIGHT: layout.cardHeight,
      CONTAINER_PADDING: layout.containerPadding,
      FONT_SIZE: layout.fontSize,
      
      // 중첩 객체 방식도 지원
      COLORS: {
        BACKGROUND: colors.background.start,
        BACKGROUND_START: colors.background.start,
        BACKGROUND_END: colors.background.end,
        CARD: colors.card.start,
        CARD_START: colors.card.start,
        CARD_END: colors.card.end,
        FONT: colors.font.primary,
        FONT_PRIMARY: colors.font.primary,
        FONT_SECONDARY: colors.font.secondary
      },
      
      // 기타 설정
      SHEET_URL: extractedData?.sheetUrl || '',
      TEMPLATE_TYPE: extractedData?.templateType || 'flashcard'
    };
    
    console.log('🎨 prepareTemplateData 생성된 데이터:', templateData);
    return templateData;
  }, [wordsList, extractedData]);

  // iframe에서 현재 적용된 디자인 설정 추출
  const extractCurrentDesignFromIframe = useCallback((iframeDocument) => {
    try {

      
      // 기본값
      const defaultSettings = {
        background: { start: '#667eea', end: '#764ba2' },
        card: { start: '#4facfe', end: '#00f2fe' },
        font: { primary: '#333333', secondary: '#666666' }
      };
      
      // body의 background에서 그라데이션 추출
      const bodyStyle = iframeDocument.body?.style;
      if (bodyStyle && bodyStyle.background) {
        const bgMatch = bodyStyle.background.match(/linear-gradient\([^)]+\)/);
        if (bgMatch) {
          const gradientColors = bgMatch[0].match(/#[a-fA-F0-9]{6}/g);
          if (gradientColors && gradientColors.length >= 2) {
            defaultSettings.background.start = gradientColors[0];
            defaultSettings.background.end = gradientColors[1];
          }
        }
      }
      
      // .card 요소에서 카드 그라데이션 추출
      const cardElements = iframeDocument.querySelectorAll('.card');
      if (cardElements.length > 0) {
        const cardStyle = cardElements[0].style;
        if (cardStyle && cardStyle.background) {
          const cardMatch = cardStyle.background.match(/linear-gradient\([^)]+\)/);
          if (cardMatch) {
            const cardColors = cardMatch[0].match(/#[a-fA-F0-9]{6}/g);
            if (cardColors && cardColors.length >= 2) {
              defaultSettings.card.start = cardColors[0];
              defaultSettings.card.end = cardColors[1];
            }
          }
        }
      }
      
      // .word, .meaning 요소에서 폰트 색상 추출
      const wordElement = iframeDocument.querySelector('.word');
      if (wordElement && wordElement.style.color) {
        defaultSettings.font.primary = wordElement.style.color;
      }
      
      const meaningElement = iframeDocument.querySelector('.meaning');
      if (meaningElement && meaningElement.style.color) {
        defaultSettings.font.secondary = meaningElement.style.color;
      }
      

      return defaultSettings;
      
    } catch (error) {
      console.error('❌ iframe 디자인 추출 실패:', error);
      return {
        background: { start: '#667eea', end: '#764ba2' },
        card: { start: '#4facfe', end: '#00f2fe' },
        font: { primary: '#ffffff', secondary: '#ffffff' }
      };
    }
  }, []);

  // AI 서버의 colors 응답을 applyDesignSettings 형태로 변환
  const transformAIColorsToDesignSettings = useCallback((aiColors) => {
    try {

      
      // AI 서버 응답 구조에 따라 변환
      const extractGradientColors = (gradientString) => {
        if (!gradientString) return { start: '#667eea', end: '#764ba2' };
        
        const colors = gradientString.match(/#[a-fA-F0-9]{6}/g);
        if (colors && colors.length >= 2) {
          return { start: colors[0], end: colors[1] };
        }
        
        // 단일 색상인 경우 그라데이션으로 변환
        if (colors && colors.length === 1) {
          return { start: colors[0], end: colors[0] };
        }
        
        return { start: '#667eea', end: '#764ba2' };
      };
      
      const designSettings = {
        background: aiColors.background 
          ? extractGradientColors(aiColors.background.gradient || aiColors.background)
          : { start: '#667eea', end: '#764ba2' },
        
        card: aiColors.card 
          ? extractGradientColors(aiColors.card.gradient || aiColors.card)
          : { start: '#4facfe', end: '#00f2fe' },
        
        font: {
          primary: aiColors.font?.primary || '#333333',
          secondary: aiColors.font?.secondary || '#666666'
        }
      };
      

      return designSettings;
      
    } catch (error) {
      console.error('❌ AI colors 변환 실패:', error);
      return {
        background: { start: '#667eea', end: '#764ba2' },
        card: { start: '#4facfe', end: '#00f2fe' },
        font: { primary: '#333333', secondary: '#666666' }
      };
    }
  }, []);

  // 새로운 템플릿 기반 HTML 생성 함수
  const generateHTMLFromTemplate = useCallback(async (editedData, designSettings = {}) => {
    try {
      // 템플릿 타입 결정 - 항상 editor 템플릿 사용 (placeholder가 있는 버전)
      const editorTemplatePath = `/templates/editor/flashcard-template.html`;
      console.log('📁 editor 템플릿 경로 시도:', editorTemplatePath);
      
      // editor 템플릿 파일 로드
      const editorResponse = await fetch(editorTemplatePath);
      if (!editorResponse.ok) {
        throw new Error(`editor 템플릿 파일을 찾을 수 없습니다: ${editorTemplatePath}`);
      }
      
      const templateHTML = await editorResponse.text();
      console.log('✅ editor 템플릿 로드 성공, 길이:', templateHTML.length);

      
      // 템플릿 데이터 준비
      const templateData = prepareTemplateData(editedData, designSettings);
      
      // 템플릿 렌더링
      const finalHTML = renderTemplate(templateHTML, templateData);
      

      return finalHTML;
      
    } catch (error) {
      console.error('❌ 템플릿 기반 HTML 생성 실패:', error);
      throw error;
    }
  }, [extractedData, prepareTemplateData, renderTemplate]);

  // 실시간 동기화: AI편집 → 학습용 미리보기 변환 (템플릿 기반)
  const convertToLearningTemplate = useCallback((editedData) => {
    try {
      // 기본 폴백: 기존 htmlContent 사용
      let updatedHTML = htmlContent;
      
      // 템플릿 기반 처리
      if (editedData.words || editedData.style || editedData.layout) {

        
        // 템플릿 데이터 준비
        const templateData = prepareTemplateData(editedData);
        
        // 기존 HTML에서 템플릿 변수들을 교체
        updatedHTML = renderTemplate(updatedHTML, templateData);
        

      }
      

      return updatedHTML;
    } catch (error) {
      console.error('❌ 학습용 템플릿 변환 실패:', error);
      return htmlContent; // 실패 시 원본 반환
    }
  }, [htmlContent, prepareTemplateData, renderTemplate]);


  // AI 편집 완료 후 호출되는 함수 (개선된 버전)
  const handleAIEditComplete = useCallback((updatedHTML) => {
    try {
      // AI 편집된 HTML에서 데이터 추출
      const editIframe = editIframeRef.current;
      if (!editIframe) return;

      const editDoc = editIframe.contentDocument || editIframe.contentWindow.document;
      const templateDataScript = editDoc.getElementById('template-data');
      
      if (templateDataScript) {
        const editedData = JSON.parse(templateDataScript.textContent);
        const rawWords = editedData.words || editedData.vocabularyData || [];
        const newWordsList = rawWords.map(item => ({
          word: item.word || item.english || '',
          meaning: item.meaning || item.korean || '',
          example: item.example || item.sentence || ''
        }));
        
        // 1. 단어 목록 상태 업데이트
        setWordsList(newWordsList);
        
        // 2. 선택된 요소 타입에 따른 함수 호출
        const selectedElementType = selectedElement?.editType;

        
        if (selectedElementType && editIframe.contentWindow) {
          try {
            switch(selectedElementType) {
              case 'vocabulary':

                break;
                
              case 'style':

                if (editedData.style) {
                  Object.entries(editedData.style).forEach(([colorType, colorValue]) => {

                    editIframe.contentWindow.postMessage({
                      type: 'CHANGE_COLOR',
                      data: { colorType, colorValue }
                    }, '*');
                  });
                }
                break;
                
              case 'layout':

                // AI 응답에서 직접 레이아웃 값을 파싱해서 적용
                if (editedData.layout) {
                  Object.entries(editedData.layout).forEach(([layoutType, layoutValue]) => {

                    editIframe.contentWindow.postMessage({
                      type: 'CHANGE_LAYOUT',
                      data: { layoutType, layoutValue }
                    }, '*');
                  });
                } else {
                  // AI 응답에서 레이아웃 변경 키워드 감지하여 수동 적용

                  editIframe.contentWindow.postMessage({
                    type: 'CHANGE_LAYOUT',
                    data: { layoutType: 'size', layoutValue: 'large' }
                  }, '*');
                }
                break;
                
              default:

            }
          } catch (e) {

          }
        }

        // 3. extractedData 업데이트 (우측 패널 업데이트용)
        const newExtractedData = {
          ...extractedData,
          vocabularyData: newWordsList,
          words: newWordsList,
          layoutData: editedData.layout || extractedData.layoutData
        };
        setExtractedData(newExtractedData);
        
        // 4. 메인 HTML 콘텐츠도 업데이트
        const newMainHTML = convertToLearningTemplate(editedData);
        setHtmlContent(newMainHTML);
        
        // 4. 히스토리 업데이트
        const newHistory = [...history.slice(0, currentHistoryIndex + 1), newMainHTML];
        setHistory(newHistory);
        setCurrentHistoryIndex(newHistory.length - 1);
        

      }
    } catch (error) {
      console.error('❌ AI 편집 완료 처리 실패:', error);
    }
  }, [convertToLearningTemplate, history, currentHistoryIndex, extractedData]);

  // 정리 작업
  useEffect(() => {
    return () => {
      if (editIframeRef.current) {
        cleanupElementSelector(editIframeRef.current);
      }
    };
  }, []);
  
  // 초기 콘텐츠 처리
  useEffect(() => {
    if (initialHtmlContent && !htmlContent) {
      processHTMLContent(initialHtmlContent);
    }
  }, [initialHtmlContent, htmlContent, processHTMLContent]);


  // AI 편집 iframe 새로고침 헬퍼 함수
  const refreshAIEditingIframe = useCallback((newWordsList) => {
    try {
      // AI 편집 템플릿을 새로 생성
      const updatedExtractedData = {
        ...extractedData,
        vocabularyData: newWordsList,
        words: newWordsList
      };
      
      // AI 편집 템플릿 새로 로드 (완전 새로고침)
      loadAIEditingTemplate(extractedData.templateType, updatedExtractedData);
      

      
    } catch (error) {
      console.error('❌ AI 편집 iframe 새로고침 실패:', error);
    }
  }, [extractedData, loadAIEditingTemplate]);

  // 새 단어 추가 핸들러 (CSS 변수 보존 버전)
  const handleAddWord = useCallback((newWord, replaceMode = false) => {
    try {

      
      // 데이터 구조 정규화 (항상 word, meaning, example 구조로 통일)
      const normalizedWord = {
        word: newWord.word || newWord.english || '',
        meaning: newWord.meaning || newWord.korean || '',
        example: newWord.example || newWord.sentence || ''
      };
      
      // 1. 단어 목록 상태 업데이트
      const newWordsList = replaceMode ? [normalizedWord] : [...wordsList, normalizedWord];
      setWordsList(newWordsList);
      
      // 2. extractedData 업데이트 (통일된 구조로)
      const newExtractedData = {
        ...extractedData,
        vocabularyData: newWordsList,
        words: newWordsList
      };
      setExtractedData(newExtractedData);
      
      // 3. CSS 변수를 보존하면서 단어 데이터만 업데이트 (비동기로 실행)
      setTimeout(() => {
        updateIframeVocabularyData(newWordsList);
      }, 100);
      

      
    } catch (error) {
      console.error('❌ 새 단어 추가 실패:', error);
      setError('단어 추가에 실패했습니다: ' + error.message);
    }
  }, [wordsList, extractedData, updateIframeVocabularyData]);

  // 단어 업데이트 핸들러 (CSS 변수 보존 버전)
  const handleWordUpdate = useCallback((index, updatedWord) => {
    try {
      const newWordsList = [...wordsList];
      newWordsList[index] = {
        word: updatedWord.word ||updatedWord.english || '',
        meaning: updatedWord.meaning || updatedWord.korean || '',
        example: updatedWord.example || ''
      };
      
      setWordsList(newWordsList);
      
      // extractedData 업데이트
      const newExtractedData = {
        ...extractedData,
        vocabularyData: newWordsList,
        words: newWordsList
      };
      setExtractedData(newExtractedData);
      
      // CSS 변수를 보존하면서 단어 데이터만 업데이트
      setTimeout(() => {
        updateIframeVocabularyData(newWordsList);
      }, 100);
      

    } catch (error) {
      console.error('❌ 단어 업데이트 실패:', error);
      setError('단어 업데이트에 실패했습니다: ' + error.message);
    }
  }, [wordsList, extractedData, updateIframeVocabularyData]);

  // 단어 삭제 핸들러 (CSS 변수 보존 버전)
  const handleWordDelete = useCallback((index) => {
    try {
      const newWordsList = wordsList.filter((_, i) => i !== index);
      
      setWordsList(newWordsList);
      
      // extractedData 업데이트
      const newExtractedData = {
        ...extractedData,
        vocabularyData: newWordsList,
        words: newWordsList
      };
      setExtractedData(newExtractedData);
      
      // CSS 변수를 보존하면서 단어 데이터만 업데이트
      setTimeout(() => {
        updateIframeVocabularyData(newWordsList);
      }, 100);
      

    } catch (error) {
      console.error('❌ 단어 삭제 실패:', error);
      setError('단어 삭제에 실패했습니다: ' + error.message);
    }
  }, [wordsList, extractedData, updateIframeVocabularyData]);

  // 색상 변경 핸들러
  const handleColorChange = useCallback((colorType, colorValue) => {
    try {
      const editIframe = editIframeRef.current;
      if (editIframe && editIframe.contentWindow && editIframe.contentDocument?.readyState === 'complete') {

        editIframe.contentWindow.postMessage({
          type: 'CHANGE_COLOR',
          data: { colorType, colorValue }
        }, '*');
      } else {
        console.warn('❌ iframe이 준비되지 않음');
        setTimeout(() => handleColorChange(colorType, colorValue), 100);
      }
    } catch (error) {
      console.error('색상 변경 실패:', error);
    }
  }, []);

  // 학습자료 다운로드 핸들러
  const handleDownload = useCallback(async () => {
    try {
      let finalHTML = '';
      const templateType = extractedData?.templateType || 'learning-material';
      
      // AI 편집기에서 현재 데이터 추출 (try-catch 밖에서 정의)
      let currentData = { words: wordsList };
      let currentDesignSettings = designSettings; // 부모의 designSettings 사용
      console.log('🎨 현재 designSettings:', currentDesignSettings);
      console.log('🤖 AI designSettings:', aiDesignSettings);
      
      // AI 채팅으로 저장된 디자인 설정이 있으면 우선 사용
      if (aiDesignSettings) {
        currentDesignSettings = { ...designSettings, ...aiDesignSettings };
        console.log('🔄 병합된 designSettings:', currentDesignSettings);
      } else {
        console.log('✅ designSettings만 사용 (AI 설정 없음)');
      }
      
      try {
        
        // iframe에서 추가 데이터 추출 시도
        if (editIframeRef.current?.contentWindow) {
          try {
            const iframeData = editIframeRef.current.contentWindow.templateData;
            if (iframeData) {
              currentData = { ...currentData, ...iframeData };
            }
          } catch (extractError) {
            console.warn('⚠️ iframe 데이터 추출 실패, 기본값 사용:', extractError);
          }
        }
        
        // 템플릿 기반 HTML 생성
        console.log('🔧 generateHTMLFromTemplate 호출, 전달할 designSettings:', currentDesignSettings);
        finalHTML = await generateHTMLFromTemplate(currentData, currentDesignSettings);
        console.log('✅ 템플릿 기반 HTML 생성 성공');
        
        // 생성된 HTML에서 CSS 변수 확인
        if (finalHTML.includes('--bg-start:')) {
          const bgStartMatch = finalHTML.match(/--bg-start:\s*([^;]+);/);
          console.log('📋 생성된 HTML의 --bg-start:', bgStartMatch ? bgStartMatch[1] : '찾을 수 없음');
        }

        
      } catch (templateError) {
        console.warn('⚠️ 템플릿 기반 생성 실패, 폴백 방식 사용:', templateError);
        
        // 폴백: 기존 방식들
        if (editIframeRef.current?.contentDocument) {
          let iframeHTML = editIframeRef.current.contentDocument.documentElement.outerHTML;
          console.log('📄 iframe HTML 사용, 색상 수동 적용 중...');
          
          // iframe HTML에 현재 designSettings 색상을 수동으로 적용
          iframeHTML = renderTemplate(iframeHTML, prepareTemplateData(currentData, currentDesignSettings));
          finalHTML = iframeHTML;

        } else if (editableHTML) {
          finalHTML = convertToLearningTemplate({ words: wordsList });

        } else {
          setError('다운로드할 데이터가 없습니다.');
          return;
        }
      }

      // HTML 정리 및 최적화
      finalHTML = finalHTML
        .replace(/<!DOCTYPE html>/i, '<!DOCTYPE html>')
        .replace(/<html[^>]*>/i, '<html>')
        .replace(/<head[^>]*>/i, '<head>')
        .replace(/<body[^>]*>/i, '<body>')
        // 불필요한 스크립트 제거 (선택 기능 관련)
        .replace(/<script[^>]*class="selection-handler"[^>]*>[\s\S]*?<\/script>/gi, '')
        // 임시 스타일 제거
        .replace(/<style[^>]*id="custom-design-styles"[^>]*>[\s\S]*?<\/style>/gi, '');
      
      // 파일명 생성 (템플릿 타입과 날짜 기반)
      const now = new Date();
      const dateStr = now.toISOString().slice(0, 10).replace(/-/g, '');
      const timeStr = now.toTimeString().slice(0, 5).replace(':', '');
      const fileName = `${templateType}_edited_${dateStr}_${timeStr}.html`;

      // Blob 생성 및 다운로드
      const blob = new Blob([finalHTML], { type: 'text/html;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      
      // URL 정리
      setTimeout(() => URL.revokeObjectURL(url), 100);
      

      
      // 성공 메시지 표시
      setError(''); // 기존 에러 클리어
      const successMsg = `📥 "${fileName}" 다운로드 완료!`;
      setError(successMsg);
      setTimeout(() => setError(''), 3000); // 3초 후 메시지 제거

    } catch (error) {
      console.error('❌ 다운로드 실패:', error);
      setError('다운로드에 실패했습니다: ' + error.message);
    }
  }, [editableHTML, extractedData, wordsList, designSettings, aiDesignSettings, generateHTMLFromTemplate, extractCurrentDesignFromIframe, convertToLearningTemplate, renderTemplate, prepareTemplateData]);

  // AI 생성 단어 적용 핸들러 (CSS 변수 보존 버전)
  const handleApplyAIWords = useCallback((aiWords, mode = 'add') => {
    try {

      
      let newWordsList;
      
      if (mode === 'replace') {
        // 전체 교체 모드: 기존 단어를 모두 지우고 새 단어로 교체
        newWordsList = aiWords.map(word => ({
          word: word.word || '',
          meaning: word.meaning || '',
          example: word.example || ''
        }));
        

      } else {
        // 추가 모드: 기존 단어에 새 단어 추가
        const normalizedWords = aiWords.map(word => ({
          word: word.word || '',
          meaning: word.meaning || '',
          example: word.example || ''
        }));
        
        newWordsList = [...wordsList, ...normalizedWords];

      }
      
      // 상태 업데이트
      setWordsList(newWordsList);
      
      const newExtractedData = {
        ...extractedData,
        vocabularyData: newWordsList,
        words: newWordsList
      };
      setExtractedData(newExtractedData);
      
      // CSS 변수를 보존하면서 단어 데이터만 업데이트 (비동기로 실행)
      setTimeout(() => {
        updateIframeVocabularyData(newWordsList);
      }, 100);
      
    } catch (error) {
      console.error('❌ AI 단어 적용 실패:', error);
      setError('AI 단어 적용에 실패했습니다: ' + error.message);
    }
  }, [wordsList, extractedData, updateIframeVocabularyData]);

  // 디자인 설정 적용 함수
  const applyDesignSettings = useCallback((settings) => {
    try {
      const editIframe = editIframeRef.current;
      if (!editIframe || !editIframe.contentWindow || editIframe.contentDocument?.readyState !== 'complete') {
        console.warn('❌ iframe이 준비되지 않아 디자인 적용 지연');
        setTimeout(() => applyDesignSettings(settings), 100);
        return;
      }


      
      // AI 채팅으로 변경된 디자인 설정 저장 (다운로드 시 사용)
      setAiDesignSettings(settings);

      
      // 배경 색상 개별 적용
      editIframe.contentWindow.postMessage({
        type: 'CHANGE_COLOR',
        data: { colorType: 'backgroundStart', colorValue: settings.background.start }
      }, '*');
      
      editIframe.contentWindow.postMessage({
        type: 'CHANGE_COLOR',
        data: { colorType: 'backgroundEnd', colorValue: settings.background.end }
      }, '*');
      
      // 카드 색상 개별 적용
      editIframe.contentWindow.postMessage({
        type: 'CHANGE_COLOR',
        data: { colorType: 'cardStart', colorValue: settings.card.start }
      }, '*');
      
      editIframe.contentWindow.postMessage({
        type: 'CHANGE_COLOR',
        data: { colorType: 'cardEnd', colorValue: settings.card.end }
      }, '*');
      
      // 폰트 색상 적용
      editIframe.contentWindow.postMessage({
        type: 'CHANGE_COLOR',
        data: { colorType: 'fontPrimary', colorValue: settings.font.primary }
      }, '*');
      
      editIframe.contentWindow.postMessage({
        type: 'CHANGE_COLOR',
        data: { colorType: 'fontSecondary', colorValue: settings.font.secondary }
      }, '*');
      

    } catch (error) {
      console.error('❌ 디자인 적용 실패:', error);
    }
  }, []);
  
  return (
    <div className="fixed inset-0 bg-gray-50 z-50">
      {/* 헤더 */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <h1 className="text-xl font-semibold text-gray-900">
              🤖 AI 학습자료 편집기
            </h1>
            
            {extractedData && (
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded">
                  {extractedData.templateType}
                </span>
                {extractedData.vocabularyData.length > 0 && (
                  <span className="bg-green-100 text-green-800 px-2 py-1 rounded">
                    단어 {extractedData.vocabularyData.length}개
                  </span>
                )}
                {extractedData.questionsData.length > 0 && (
                  <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded">
                    문제 {extractedData.questionsData.length}개
                  </span>
                )}
              </div>
            )}
          </div>
          
          <div className="flex items-center space-x-3">
            {/* 다운로드 버튼 (편집된 HTML) */}
            {editableHTML && (
              <button
                onClick={handleDownload}
                className="flex items-center space-x-2 px-5 py-2.5 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-all duration-200 shadow-md font-medium"
                title="편집된 학습자료 다운로드"
              >
                <span>파일 다운로드</span>
              </button>
            )}
            
            {/* 원본으로 되돌리기 */}
            <button
              onClick={() => {



                
                if (originalEditableHTMLRef.current && originalHtmlContent) {

                  
                  // 상수로 저장된 원본 AI편집 템플릿을 바로 복원
                  setEditableHTML(originalEditableHTMLRef.current);
                  
                  // 원본 데이터도 복원 (저장된 원본 사용)
                  if (originalExtractedData) {
                    setExtractedData(originalExtractedData);

                  } else {
                    console.warn('⚠️ 저장된 원본 데이터가 없어 다시 파싱');
                    const originalExtracted = parseLearnignHTML(originalHtmlContent);
                    setExtractedData(originalExtracted);
                  }
                  setIframeKey(prevKey => prevKey + 1); 
                  // 원본 단어 목록 복원
                  const dataToUse = originalExtractedData || parseLearnignHTML(originalHtmlContent);
                  if (dataToUse.vocabularyData && Array.isArray(dataToUse.vocabularyData)) {
                    const normalizedWords = dataToUse.vocabularyData.map(item => ({
                      word: item.word || item.english || '',
                      meaning: item.meaning || item.korean || '',
                      example: item.example || item.sentence || ''
                    }));
                    setWordsList(normalizedWords);

                  }
                  
                  // 히스토리 리셋
                  setHistory([originalHtmlContent]);
                  setCurrentHistoryIndex(0);
                  
                  // 선택 상태 초기화
                  setSelectedElement(null);
                  setShowEditPanel(false);
                  
                  // AI 디자인 설정도 초기화
                  setAiDesignSettings(null);

                  

                } else {
                  console.error('❌ 되돌리기 실패: 원본 데이터가 없습니다');
                }
              }}
              disabled={!originalEditableHTMLRef.current}
              className="flex items-center space-x-2 px-5 py-2.5 bg-gray-100 text-gray-800 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-all duration-200 shadow-md"
              title="원본으로 되돌리기"
            >
              되돌리기
            </button>
            
            {/* 파일 업로드 */}
            <button
              onClick={() => {


                if (fileInputRef.current) {
                  fileInputRef.current.click();

                } else {
                  console.error('❌ fileInputRef.current가 null입니다');
                }
              }}
              className="flex items-center space-x-2 px-5 py-2.5 bg-slate-700 text-white rounded-xl hover:bg-slate-800 transition-all duration-200 shadow-md font-medium"
            >
              <span>파일 업로드</span>
            </button>
            
            {/* 닫기 */}
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-all duration-200 shadow-sm"
            >
              ✕
            </button>
          </div>
        </div>
      </div>
      
      {/* 메인 콘텐츠 */}
      <div className="flex h-[calc(100vh-73px)]">
        {/* 미리보기 영역 */}
        <div className={`relative ${editableHTML ? 'flex-1' : 'flex-1'}`}>
          {!htmlContent ? (
            // 업로드 영역
            <div
              className="h-full flex items-center justify-center border-2 border-dashed border-gray-300 m-4 rounded-lg"
              onDrop={handleDrop}
              onDragOver={handleDragOver}
            >
              <div className="text-center max-w-2xl">
                <div className="text-6xl mb-4">🎨</div>
                <h3 className="text-2xl font-semibold text-gray-700 mb-3">
                  학습자료 AI 편집기
                </h3>
                <p className="text-gray-600 mb-4 text-lg">
                  기존 학습 HTML 파일을 업로드하여 AI로 내용을 수정해보세요
                </p>
                
                <div className="bg-blue-50 p-4 rounded-lg mb-6 text-left">
                  <h4 className="font-semibold text-blue-800 mb-2">🔧 편집 가능한 요소들:</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm text-blue-700">
                    <div>• 📚 단어장 / 플래시카드</div>
                    <div>• ❓ 퀴즈 문제</div>
                    <div>• 📖 지문 내용</div>
                    <div>• 🎵 오디오 설정</div>
                    <div>• 🎨 색상 / 스타일</div>
                    <div>• 📐 레이아웃 구조</div>
                  </div>
                </div>
                
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 font-medium text-lg shadow-lg transform hover:scale-105 transition-all duration-200"
                >
                  📁 HTML 파일 선택하기
                </button>
                
                <p className="text-gray-400 text-sm mt-4">
                  또는 파일을 이 영역에 드래그해주세요
                </p>
              </div>
            </div>
          ) : (
            // AI 편집 모드 (전체 화면)
            <div className="h-full p-4">
              <div className="relative h-full bg-white rounded-lg shadow overflow-hidden">
                <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-green-600 to-blue-600 text-white px-4 py-2 text-sm z-10">
                  <div className="flex items-center justify-between">
                    <span>✏️ AI 편집 모드</span>
                    <div className="flex items-center space-x-4">
                      <span className="text-xs opacity-75">요소 클릭으로 선택 • 우측 패널에서 편집 및 단어 관리</span>
                      <button
                        onClick={handleDownload}
                        className="bg-white bg-opacity-20 hover:bg-opacity-30 px-3 py-1 rounded text-xs transition-colors"
                      >
                        📥 다운로드
                      </button>
                    </div>
                  </div>
                </div>
                
                {isLoading && (
                  <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-20">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                      <p className="text-gray-600">AI편집 템플릿 로드 중...</p>
                    </div>
                  </div>
                )}
                
                {editableHTML ? (
                  <iframe
                    key={iframeKey}
                    ref={editIframeRef}
                    srcDoc={editableHTML}
                    className="w-full h-full border-0 pt-12"
                    onLoad={handleEditIframeLoad}
                    title="AI 편집"
                  />
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-500">
                    <div className="text-center">
                      <div className="text-4xl mb-3">⚙️</div>
                      <p>AI편집 템플릿 로드 중...</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          
        </div>
        
        {/* 우측 고정 채팅창 */}
        {editableHTML && (
          <div className="w-80 border-l border-gray-200 bg-white flex flex-col">
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-4">
              <h3 className="font-semibold">🤖 AI 편집 도우미</h3>
              <p className="text-sm opacity-75">요소를 클릭하고 자연어로 편집 요청하세요</p>
            </div>
            
            {selectedElement ? (
              <EditChatPanel
                selectedElement={selectedElement}
                extractedData={extractedData}
                originalHTML={htmlContent}
                modificationMode={modificationMode}
                onModificationModeChange={handleModificationModeChange}
                onClose={handleCloseEditPanel}
                onAIEditComplete={handleAIEditComplete}
                onApplyDesignSettings={applyDesignSettings}
                transformAIColorsToDesignSettings={transformAIColorsToDesignSettings}
                generateHTMLFromTemplate={generateHTMLFromTemplate}
                wordsList={wordsList}
                isRightPanel={true}
                editableHTML={editableHTML}
                updateIframeCSSVariables={updateIframeCSSVariables}
                updateIframeVocabularyData={updateIframeVocabularyData}
                setWordsList={setWordsList}
                setExtractedData={setExtractedData}
                editIframeRef={editIframeRef}
                onUpdate={(newContent) => {
                  // 히스토리에 추가
                  const newHistory = [...history.slice(0, currentHistoryIndex + 1), newContent];
                  setHistory(newHistory);
                  setCurrentHistoryIndex(newHistory.length - 1);
                  
                  processHTMLContent(newContent);
                }}
              />
            ) : (
              <QuickEditPanel
                extractedData={extractedData}
                wordsList={wordsList}
                designSettings={designSettings}
                setDesignSettings={setDesignSettings}
                onAddWord={handleAddWord}
                onColorChange={handleColorChange}
                onApplyDesignSettings={applyDesignSettings}
                onApplyAIWords={handleApplyAIWords}
                onWordUpdate={handleWordUpdate}
                onWordDelete={handleWordDelete}
              />
            )}
          </div>
        )}
      </div>
      
      {/* 메시지 (에러/성공) */}
      {error && (
        <div className={`fixed bottom-4 right-4 px-4 py-3 rounded shadow-lg max-w-md ${
          error.includes('다운로드 완료') 
            ? 'bg-green-100 border border-green-400 text-green-700' 
            : 'bg-red-100 border border-red-400 text-red-700'
        }`}>
          <div className="flex items-center">
            <span className="mr-2">
              {error.includes('다운로드 완료') ? '✅' : '❌'}
            </span>
            <span className="flex-1">{error}</span>
            <button
              onClick={() => setError('')}
              className={`ml-4 ${
                error.includes('다운로드 완료') 
                  ? 'text-green-500 hover:text-green-700' 
                  : 'text-red-500 hover:text-red-700'
              }`}
            >
              ✕
            </button>
          </div>
        </div>
      )}
      
      {/* 숨겨진 파일 입력 */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileUpload}
        accept=".html,.htm"
        className="hidden"
      />

    </div>
  );
};

// EditChatPanel 컴포넌트
const EditChatPanel = ({ selectedElement, extractedData, originalHTML, modificationMode = 'preserve', onModificationModeChange, onClose, onUpdate, onAIEditComplete, onApplyDesignSettings, transformAIColorsToDesignSettings, generateHTMLFromTemplate, wordsList, isRightPanel = false, editableHTML, updateIframeCSSVariables, updateIframeVocabularyData, setWordsList, setExtractedData, editIframeRef }) => {
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const messagesEndRef = useRef(null);
  
  // 메시지가 변경될 때마다 스크롤을 아래로 이동
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isProcessing]);
  
  // 편집 타입이 변경될 때 채팅 기록 초기화
  useEffect(() => {
    if (selectedElement?.editType) {

      setMessages([]);
      setMessage('');
    }
  }, [selectedElement?.editType]);
  
  const handleSendMessage = async () => {
    if (!message.trim()) return;
    
    const userMessage = message.trim();
    setMessage('');
    setMessages(prev => [...prev, { type: 'user', content: userMessage }]);
    setIsProcessing(true);
    
    try {



      
      // 선택된 요소 타입에 따라 직접 함수 호출

      
      // 모든 요청을 AI 서버로 통합 처리

      
      // 디자인/레이아웃 요청은 ai-assistant로, 기타는 기존 방식으로 처리
      let response;
      if (selectedElement.editType === 'style' || selectedElement.editType === 'layout') {

        
        // 현재 디자인 설정 추출 (컨텍스트로 전달)
        const currentDesignSettings = extractedData?.designSettings || {
          background: { start: '#667eea', end: '#764ba2' },
          card: { start: '#4facfe', end: '#00f2fe' },
          font: { primary: '#333333', secondary: '#666666' }
        };
        
        response = await fetch('/api/ai-assistant', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            mode: 'design',
            request: userMessage,
            context: {
              currentColors: currentDesignSettings,
              templateType: extractedData?.templateType || 'flashcard',
              elementType: selectedElement.editType,
              elementInfo: {
                tagName: selectedElement.tagName,
                className: selectedElement.className,
                textContent: selectedElement.textContent
              }
            }
          })
        });
      } else {
        // 새로운 section 기반 API 호출
        console.log('📚 section 기반 API 호출 - selectedElement.editType:', selectedElement.editType);
        console.log('📝 현재 wordsList:', wordsList);
        
        response = await fetch('/api/edit-html-sectional', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            mode: selectedElement.editType, // 'vocabulary' 또는 기타
            section: selectedElement.section || selectedElement.styleSection?.replace('style-', ''), // 'words', 'background', 'card', 'font', 'cardsize'  
            userRequest: userMessage,
            htmlContent: editableHTML, // 현재 iframe HTML
            modificationMode: modificationMode, // 단어 수정 모드 추가
            currentWords: wordsList || [], // 현재 단어 목록 전달
            gradeLevel: extractedData?.gradeLevel || '초등3학년', // 등급 정보 전달
            currentValues: extractedData?.currentValues || {}, // 현재 디자인 값들 전달
            selectedElement: {
            xpath: selectedElement.xpath,
            section: selectedElement.section,
            tagName: selectedElement.tagName,
            className: selectedElement.className,
            innerHTML: selectedElement.innerHTML,
            textContent: selectedElement.textContent,
            data: selectedElement.data
          },
          selectedCodeContext: `사용자가 선택한 요소의 정보:
- 요소 타입: ${selectedElement.tagName}
- 클래스: ${selectedElement.className}  
- 내용: ${selectedElement.textContent || selectedElement.innerHTML}
- 편집 유형: ${selectedElement.editType}
- 수정 모드: ${modificationMode} ${modificationMode === 'preserve' ? '(단어 보존 모드 - 단어와 의미는 유지하고 예문만 수정)' : '(전체 수정 모드 - 모든 내용 수정 가능)'}
- 데이터: ${JSON.stringify(selectedElement.data, null, 2)}

${modificationMode === 'preserve' ? 
  '⚠️ 중요: 현재 단어(word)와 의미(meaning)는 절대 변경하지 말고, 예문(example)이나 추가 설명만 수정하세요.' :
  '이 선택된 요소를 기반으로 사용자의 요청을 처리해야 합니다.'
}`,
          originalHTML: originalHTML
        })
        });
      }
      
      if (!response.ok) {
        throw new Error(`AI 요청 실패: ${response.status}`);
      }
      
      const result = await response.json();

      
      if (result.success) {
        // 새로운 섹션 기반 응답 처리
        console.log('🎯 섹션별 편집 응답:', result);
        console.log('🔍 응답 모드 확인:', result.mode);
        
        // 디자인 모드 처리
        if (result.mode === 'design' && result.changes?.cssUpdates) {
          console.log('🎨 디자인 섹션 실시간 반영:', result.section);
          console.log('➡️ 디자인 모드 조건 1 진입');
          
          // iframe 내 CSS 변수 실시간 업데이트
          updateIframeCSSVariables(result.changes.cssUpdates);
          
          // 디자인 수정 성공 메시지 추가
          setMessages(prev => [...prev, { 
            type: 'assistant', 
            content: `✅ ${result.section} 디자인이 업데이트되었습니다!\n\n${result.explanation || '변경사항이 화면에 반영되었습니다.'}` 
          }]);
        }
        
        // 단어 모드 처리  
        else if (result.mode === 'vocabulary') {
          console.log('📚 단어 섹션 실시간 반영');
          
          // 단어 데이터가 있으면 iframe 업데이트 및 상태 동기화
          if (result.words && result.words.length > 0) {
            // CSS 변수 보존을 위해 iframe 내 단어 데이터만 업데이트
            updateIframeVocabularyData(result.words);
            
            // 단어 업데이트 후 CSS 변수가 리셋될 수 있으므로 강제로 다시 적용
            setTimeout(() => {
              const iframe = editIframeRef?.current;
              if (iframe && iframe.contentDocument) {
                const rootStyle = iframe.contentDocument.documentElement.style;
                
                // 기존에 적용된 CSS 변수들을 다시 확인하고 재적용
                console.log('🎨 단어 업데이트 후 CSS 변수 재확인');
                
                // 만약 CSS 변수가 리셋되었다면 updateIframeCSSVariables 호출
                const bgStart = rootStyle.getPropertyValue('--bg-start');
                if (!bgStart || bgStart === '') {
                  console.log('🔄 CSS 변수 리셋 감지, 재적용 필요');
                  // 여기서 마지막으로 적용된 CSS 변수들을 다시 적용해야 함
                }
              }
            }, 100);
            
            // wordsList 상태 업데이트 (중요!)
            const normalizedWords = result.words.map(item => ({
              word: item.word || item.english || '',
              meaning: item.meaning || item.korean || '',
              example: item.example || item.sentence || ''
            }));
            
            // wordsList 상태 업데이트 (이게 없어서 다음 API 호출에 반영 안됨)
            if (typeof setWordsList === 'function') {
              setWordsList(normalizedWords);
              console.log('📝 wordsList 상태 업데이트 완료:', normalizedWords.length, '개');
            }
            
            // extractedData도 동기화
            if (extractedData && typeof setExtractedData === 'function') {
              const updatedExtractedData = {
                ...extractedData,
                vocabularyData: normalizedWords
              };
              setExtractedData(updatedExtractedData);
              console.log('🔄 extractedData 동기화 완료');
            }
          }
          
          // 단어 수정 성공 메시지 추가
          setMessages(prev => [...prev, { 
            type: 'assistant', 
            content: `✅ 단어 처리 완료!\n\n${result.explanation || '요청이 처리되었습니다.'}` 
          }]);
        }
        
        // 기존 디자인 요청 처리 (하위 호환성)
        else if ((selectedElement.editType === 'style' || selectedElement.editType === 'layout') && result.colors && onApplyDesignSettings) {
          const designSettings = transformAIColorsToDesignSettings(result.colors);
          onApplyDesignSettings(designSettings);
          
          try {
            const updatedHTML = await generateHTMLFromTemplate({ words: wordsList }, designSettings);
            if (updatedHTML && onUpdate) {
              onUpdate(updatedHTML);
            }
            if (onAIEditComplete && updatedHTML) {
              onAIEditComplete(updatedHTML);
            }
          } catch (updateError) {
            console.warn('⚠️ iframe 내용 갱신 실패:', updateError);
          }
          
          setMessages(prev => [...prev, { 
            type: 'assistant', 
            content: `✅ 디자인이 변경되었습니다!\n\n색상 조합이 화면에 반영되었습니다.` 
          }]);
        } 
        // 단어 모드와 디자인 모드가 아닌 경우에만 HTML 전체 업데이트
        else if (result.mode !== 'vocabulary' && result.mode !== 'design') {
          setMessages(prev => [...prev, { 
            type: 'assistant', 
            content: `✅ 요청이 처리되었습니다!\n\n변경사항이 화면에 반영되었습니다.` 
          }]);
          
          // 기존 방식: 업데이트된 HTML을 부모 컴포넌트에 전달
          if (result.updatedHTML) {
            onUpdate(result.updatedHTML);
          }
          
          // AI 편집 완료 처리 (실시간 동기화)
          if (onAIEditComplete && result.updatedHTML) {
            onAIEditComplete(result.updatedHTML);
          }
        }
      } else {
        throw new Error(result.error || '알 수 없는 오류');
      }
      
    } catch (error) {
      console.error('AI 편집 실패:', error);
      setMessages(prev => [...prev, { 
        type: 'error', 
        content: '❌ 처리 중 오류가 발생했습니다: ' + error.message 
      }]);
    } finally {
      setIsProcessing(false);
    }
  };
  
  // 현재 섹션의 데이터 추출
  const getCurrentSectionData = (extractedData, sectionType) => {
    if (!extractedData) return {};
    
    switch (sectionType) {
      case 'vocabulary':
        return extractedData.vocabularyData || [];
      case 'questions':
        return extractedData.questionsData || [];
      case 'passage':
        return extractedData.passageData || {};
      case 'audio':
        return extractedData.audioData || [];
      case 'style':
        return extractedData.styleData || {};
      case 'layout':
        // 현재 HTML에서 실제 레이아웃 정보 추출
        const currentLayoutData = extractedData.layoutData || {};
        
        // iframe에서 현재 레이아웃 상태도 가져오기 시도
        try {
          const iframe = document.querySelector('iframe[data-edit-type]');
          if (iframe && iframe.contentWindow) {
            const iframeTemplateData = iframe.contentWindow.templateData;
            if (iframeTemplateData && iframeTemplateData.layout) {
              return {
                ...currentLayoutData,
                current: iframeTemplateData.layout,
                description: {
                  grid: iframeTemplateData.layout.grid || '현재 그리드 설정 없음',
                  size: iframeTemplateData.layout.size || '현재 크기 설정 없음', 
                  spacing: iframeTemplateData.layout.spacing || '현재 간격 설정 없음'
                }
              };
            }
          }
        } catch (e) {

        }
        
        return {
          ...currentLayoutData,
          current: {},
          description: '현재 기본 레이아웃 (그리드: auto-fill, 크기: 300px+, 간격: 20px)'
        };
      default:
        return {};
    }
  };
  
  return (
    <div className={`${isRightPanel ? 'flex flex-col h-full' : 'w-96 border-l border-gray-200'} bg-white flex flex-col min-h-0`}>
      {/* 패널 헤더 */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">
            ✏️ {selectedElement.data?.type || selectedElement.editType} 편집
          </h3>
          <div className="flex items-center space-x-2">
            {/* 채팅 초기화 버튼 */}
            {messages.length > 0 && (
              <button
                onClick={() => {
                  setMessages([]);
                  setMessage('');

                }}
                className="text-gray-400 hover:text-gray-600 text-sm p-1 rounded hover:bg-gray-100"
                title="채팅 기록 초기화"
              >
                🧹
              </button>
            )}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-xl"
              title="닫기"
            >
              ×
            </button>
          </div>
        </div>
        
        {/* 선택된 요소 정보 */}
        <div className="mt-2 text-sm text-gray-600">
          <p><strong>유형:</strong> {selectedElement.editType}</p>
          <p><strong>섹션:</strong> {selectedElement.section}</p>
          {selectedElement.data?.content && (
            <p className="truncate"><strong>내용:</strong> {selectedElement.data.content}</p>
          )}
          
          {/* 단어 카드 선택 시 수정 모드 선택 */}
          {(selectedElement.isWordCard || selectedElement.section === 'word-card' || 
            (selectedElement.editType === 'vocabulary' && selectedElement.data?.type === 'vocabulary')) && (
            <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="text-xs font-medium text-blue-700 mb-2">단어 수정 모드 선택:</div>
              <div className="space-y-2">
                <button
                  onClick={() => onModificationModeChange && onModificationModeChange('preserve')}
                  className={`w-full p-2 text-xs rounded-lg border-2 transition-colors text-left ${
                    modificationMode === 'preserve' 
                      ? 'border-blue-400 bg-blue-100 text-blue-800' 
                      : 'border-blue-200 bg-white text-blue-600 hover:border-blue-300 hover:bg-blue-50'
                  }`}
                >
                  <div className="font-medium">🛡️ 현재 단어 유지</div>
                  <div className="text-xs opacity-75 mt-1">단어와 의미는 그대로 두고 예문만 추가/수정</div>
                </button>
                
                <button
                  onClick={() => onModificationModeChange && onModificationModeChange('modify')}
                  className={`w-full p-2 text-xs rounded-lg border-2 transition-colors text-left ${
                    modificationMode === 'modify' 
                      ? 'border-orange-400 bg-orange-100 text-orange-800' 
                      : 'border-orange-200 bg-white text-orange-600 hover:border-orange-300 hover:bg-orange-50'
                  }`}
                >
                  <div className="font-medium">✏️ 단어 전체 수정</div>
                  <div className="text-xs opacity-75 mt-1">단어, 의미, 예문을 모두 자유롭게 수정</div>
                </button>
              </div>
            </div>
          )}
          
          {/* 스타일/레이아웃 섹션 선택 시 안내 */}
          {(selectedElement.editType === 'style' || selectedElement.editType === 'layout') && (
            <div className="mt-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
              <div className="text-xs font-medium text-purple-700 mb-2">
                {selectedElement.editType === 'style' ? '🎨 디자인 설정' : '📐 레이아웃 설정'}
              </div>
              <div className="text-xs text-purple-600">
                {selectedElement.editType === 'style' ? (
                  <div>
                    <p className="mb-2">현재 색상 테마를 수정할 수 있습니다:</p>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li>배경 그라디언트 색상 변경</li>
                      <li>카드 색상 테마 조정</li>
                      <li>폰트 색상 설정</li>
                      <li>전체적인 컬러 톤 변경</li>
                    </ul>
                  </div>
                ) : (
                  <div>
                    <p className="mb-2">레이아웃을 조정할 수 있습니다:</p>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li>카드 크기 및 배치 방식</li>
                      <li>간격과 여백 조정</li>
                      <li>그리드 레이아웃 변경</li>
                      <li>반응형 설정 수정</li>
                    </ul>
                  </div>
                )}
                <p className="mt-2 text-purple-500 font-medium">
                  💬 아래에서 원하는 변경사항을 자연어로 입력하세요
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* 채팅 메시지들 */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 min-h-0">
        {messages.length === 0 && (
          <div className="text-center text-gray-500 py-8">
            <div className="text-2xl mb-2">💬</div>
            <p className="text-sm">
              {selectedElement.editType === 'vocabulary' && selectedElement.data?.type === 'vocabulary'
                ? '단어 수정 모드를 선택하고 아래에서 수정 요청을 입력하세요'
                : selectedElement.editType === 'style'
                ? '배경색, 카드색, 폰트 등 디자인 수정 요청을 입력하세요'
                : selectedElement.editType === 'layout'
                ? '카드 크기, 배치, 간격 등 레이아웃 수정 요청을 입력하세요'
                : '자연어로 수정 요청을 입력하세요'}
            </p>
          </div>
        )}
        
        {messages.map((msg, index) => (
          <div
            key={index}
            className={`p-3 rounded-lg ${
              msg.type === 'user'
                ? 'bg-blue-100 text-blue-800 ml-4'
                : msg.type === 'error'
                ? 'bg-red-100 text-red-800'
                : 'bg-gray-100 text-gray-800 mr-4'
            }`}
          >
            {msg.content}
          </div>
        ))}
        
        {isProcessing && (
          <div className="bg-gray-100 text-gray-800 mr-4 p-3 rounded-lg">
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
              처리 중...
            </div>
          </div>
        )}
        
        {/* 스크롤 타겟 */}
        <div ref={messagesEndRef} />
      </div>
      
      {/* 메시지 입력 - 하단 고정 */}
      <div className="shrink-0 p-4 border-t border-gray-200 bg-white">
        <div className="flex space-x-2">
          <input
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
            placeholder="수정 요청을 입력하세요..."
            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={isProcessing}
          />
          <button
            onClick={handleSendMessage}
            disabled={isProcessing || !message.trim()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            전송
          </button>
        </div>
      </div>
    </div>
  );
};

// QuickEditPanel 컴포넌트 (개선된 버전)
const QuickEditPanel = ({ extractedData, wordsList = [], designSettings, setDesignSettings, onAddWord, onColorChange, onApplyDesignSettings, onApplyAIWords, onWordUpdate, onWordDelete }) => {
  const [activePanel, setActivePanel] = useState(null); // 'addWord', 'aiGenerateWords', 'colorTheme', 'wordList'
  const [newWordForm, setNewWordForm] = useState({
    word: '',
    meaning: '',
    example: ''
  });
  
  // AI 단어 생성 상태
  const [aiWordRequest, setAiWordRequest] = useState('');
  const [isGeneratingWords, setIsGeneratingWords] = useState(false);
  
  // Section 선택 상태 추가
  const [selectedVocabularySection, setSelectedVocabularySection] = useState('words');
  const [selectedDesignSection, setSelectedDesignSection] = useState('background');
  const [aiGeneratedWords, setAiGeneratedWords] = useState([]);
  
  const [isApplyingDesign, setIsApplyingDesign] = useState(false);
  const [aiDesignRequest, setAiDesignRequest] = useState('');

  // 직접 단어 추가 핸들러
  const handleSubmit = (e) => {
    e.preventDefault();
    if (newWordForm.word.trim() && newWordForm.meaning.trim()) {
      onAddWord(newWordForm);
      setNewWordForm({ word: '', meaning: '', example: '' });
    }
  };
  
  // AI 단어 생성 핸들러
  const handleAIGenerateWords = async () => {
    if (!aiWordRequest.trim()) return;
    setIsGeneratingWords(true);
    try {
      const response = await fetch('/api/ai-assistant', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          mode: 'vocabulary',
          request: aiWordRequest,
          context: {
            currentWords: wordsList,
            templateType: extractedData?.templateType || 'flashcard',
            difficulty: 'intermediate',
            count: 10
          }
        })
      });
      
      if (!response.ok) {
        throw new Error(`AI 단어 생성 실패: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.success && result.words) {
        setAiGeneratedWords(result.words);

      } else {
        throw new Error(result.error || '알 수 없는 오류');
      }
      
    } catch (error) {
      console.error('❌ AI 단어 생성 실패:', error);
      alert('AI 단어 생성에 실패했습니다: ' + error.message);
    } finally {
      setIsGeneratingWords(false);
    }
  };
  
  
  // 색상 변경 핸들러 (개선된 버전)
  const handleColorChange = (category, type, color) => {
    console.log('🎨 색상 변경 요청:', { category, type, color });
    
    // 즉시 iframe에 색상 변경 적용
    if (onColorChange) {
      // iframe에서 기대하는 형식으로 변환
      const colorTypeMap = {
        'background_start': 'backgroundStart',
        'background_end': 'backgroundEnd', 
        'card_start': 'cardStart',
        'card_end': 'cardEnd',
        'font_primary': 'fontPrimary',
        'font_secondary': 'fontSecondary'
      };
      const colorType = colorTypeMap[`${category}_${type}`] || `${category}${type.charAt(0).toUpperCase()}${type.slice(1)}`;
      onColorChange(colorType, color);
    }
    
    // 부모 컴포넌트의 setDesignSettings 호출
    const newSettings = {
      ...designSettings,
      [category]: {
        ...designSettings[category],
        [type]: color
      }
    };
    
    console.log('🔄 부모 designSettings 업데이트:', newSettings);
    setDesignSettings(newSettings);
    
    // 디자인 설정 변경 시 즉시 적용
    setTimeout(() => onApplyDesignSettings && onApplyDesignSettings(newSettings), 50);
  };
  
  // AI 디자인 생성 핸들러
  const handleAIGenerateDesign = async () => {
    if (!aiDesignRequest.trim()) return;
    
    setIsApplyingDesign(true);
    try {

      
      const response = await fetch('/api/ai-assistant', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          mode: 'design',
          request: aiDesignRequest,
          context: {
            currentColors: designSettings,
            templateType: extractedData?.templateType || 'flashcard'
          }
        })
      });
      
      if (!response.ok) {
        throw new Error(`AI 디자인 생성 실패: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.success && result.colors) {
        // AI 생성 색상을 designSettings에 적용
        const aiColors = result.colors;
        setDesignSettings({
          background: {
            start: extractColorFromGradient(aiColors.background.gradient, 0),
            end: extractColorFromGradient(aiColors.background.gradient, 1)
          },
          card: {
            start: extractColorFromGradient(aiColors.card.gradient, 0),
            end: extractColorFromGradient(aiColors.card.gradient, 1)
          },
          font: {
            primary: aiColors.font.primary,
            secondary: aiColors.font.secondary
          }
        });
        

      } else {
        throw new Error(result.error || '알 수 없는 오류');
      }
      
    } catch (error) {
      console.error('❌ AI 디자인 생성 실패:', error);
      alert('AI 디자인 생성에 실패했습니다: ' + error.message);
    } finally {
      setIsApplyingDesign(false);
    }
  };
  
  // 그라데이션에서 색상 추출 함수
  const extractColorFromGradient = (gradient, index) => {
    const colorMatch = gradient.match(/#[0-9a-fA-F]{6}/g);
    return colorMatch && colorMatch[index] ? colorMatch[index] : '#000000';
  };
  

  // 디자인 적용 핸들러 (버튼용)
  const handleApplyDesign = () => {
    onApplyDesignSettings && onApplyDesignSettings(designSettings);

  };

  if (!extractedData || extractedData.templateType !== 'flashcard') {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="text-center text-gray-500">
          <div className="text-4xl mb-3">👆</div>
          <p className="text-sm">
            좌측에서 편집하고 싶은<br/>
            요소를 클릭해주세요
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 p-4 space-y-4 overflow-y-auto">
      {/* 기본 상태: 빠른 편집 메뉴 */}
      {!activePanel && (
        <div className="space-y-3">
          <div className="text-center text-gray-500 mb-6">
            <div className="text-3xl mb-2">⚡</div>
            <p className="text-sm font-medium">빠른 편집</p>
            <p className="text-xs">아래 버튼을 클릭하여 편집하세요</p>
          </div>
          
          <div className="space-y-2">
            <button
              onClick={() => setActivePanel('aiGenerateWords')}
              className="w-full bg-green-50 hover:bg-green-100 border border-green-200 text-green-700 py-3 rounded-lg transition-colors flex items-center justify-center space-x-2"
            >
              <span>🤖</span>
              <span>AI와 함께 새단어 추가하기</span>
            </button>
            
            <button
              onClick={() => setActivePanel('addWord')}
              className="w-full bg-yellow-50 hover:bg-yellow-100 border border-yellow-200 text-yellow-700 py-3 rounded-lg transition-colors flex items-center justify-center space-x-2"
            >
              <span>✏️</span>
              <span>직접 단어 추가하기</span>
            </button>
            
            <button
              onClick={() => setActivePanel('colorTheme')}
              className="w-full bg-purple-50 hover:bg-purple-100 border border-purple-200 text-purple-700 py-3 rounded-lg transition-colors flex items-center justify-center space-x-2"
            >
              <span>🎨</span>
              <span>디자인 설정</span>
            </button>
            
            <button
              onClick={() => setActivePanel('wordList')}
              className="w-full bg-blue-50 hover:bg-blue-100 border border-blue-200 text-blue-700 py-3 rounded-lg transition-colors flex items-center justify-center space-x-2"
            >
              <span>📚</span>
              <span>현재 단어 목록 ({wordsList.length}개)</span>
            </button>
          </div>
        </div>
      )}

      {/* AI 단어 생성 패널 */}
      {activePanel === 'aiGenerateWords' && (
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-gray-800 flex items-center">
              <span className="mr-2">🤖</span>
              AI 단어 생성
            </h4>
            <button
              onClick={() => setActivePanel(null)}
              className="text-gray-400 hover:text-gray-600 text-xl"
            >
              ×
            </button>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-2">
                어떤 단어를 생성하고 싶나요?
              </label>
              <textarea
                value={aiWordRequest}
                onChange={(e) => setAiWordRequest(e.target.value)}
                placeholder="예: '음식 관련 단어 10개 생성해줘', '고급 비즈니스 영어 단어들 만들어줘', '여행할 때 필요한 기초 단어들'"
                className="w-full border border-gray-300 rounded px-3 py-2 h-20 resize-none focus:outline-none focus:ring-2 focus:ring-green-500"
                disabled={isGeneratingWords}
              />
              <p className="text-xs text-gray-500 mt-1">
                원하는 주제, 난이도, 개수를 자유롭게 설명해주세요
              </p>
            </div>
            
            <div className="flex space-x-2">
              <button
                onClick={handleAIGenerateWords}
                disabled={!aiWordRequest.trim() || isGeneratingWords}
                className="flex-1 bg-green-600 text-white py-2 rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2"
              >
                {isGeneratingWords ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>생성 중...</span>
                  </>
                ) : (
                  <>
                    <span>🎲</span>
                    <span>단어 생성하기</span>
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={() => setActivePanel(null)}
                className="px-4 py-2 border border-gray-300 text-gray-600 rounded hover:bg-gray-50 transition-colors"
              >
                취소
              </button>
            </div>
            
            {/* 생성된 단어 미리보기 */}
            {aiGeneratedWords.length > 0 && (
              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <h5 className="font-medium text-gray-700 mb-2 flex items-center">
                  <span className="mr-1">✨</span>
                  생성된 단어 ({aiGeneratedWords.length}개)
                </h5>
                <div className="max-h-32 overflow-y-auto space-y-1">
                  {aiGeneratedWords.map((word, index) => (
                    <div key={index} className="text-sm bg-white p-2 rounded border">
                      <span className="font-medium text-blue-600">{word.word}</span>
                      <span className="mx-2">-</span>
                      <span className="text-gray-600">{word.meaning}</span>
                    </div>
                  ))}
                </div>
                
                <div className="flex space-x-2 mt-3">
                  <button
                    onClick={() => {
                      onApplyAIWords(aiGeneratedWords, 'add');
                      setAiGeneratedWords([]);
                      setAiWordRequest('');
                      setActivePanel(null);
                    }}
                    className="flex-1 bg-blue-600 text-white py-2 px-3 rounded hover:bg-blue-700 text-sm"
                  >
                    기존 단어에 추가
                  </button>
                  <button
                    onClick={() => {
                      onApplyAIWords(aiGeneratedWords, 'add');
                      setAiGeneratedWords([]);
                      setAiWordRequest('');
                      setActivePanel(null);
                    }}
                    className="flex-1 bg-orange-600 text-white py-2 px-3 rounded hover:bg-orange-700 text-sm"
                  >
                    전체 교체
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 직접 단어 추가 폼 */}
      {activePanel === 'addWord' && (
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-gray-800 flex items-center">
              <span className="mr-2">➕</span>
              새 단어 추가
            </h4>
            <button
              onClick={() => setActivePanel(null)}
              className="text-gray-400 hover:text-gray-600 text-xl"
            >
              ×
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                단어 (영어)
              </label>
              <input
                type="text"
                value={newWordForm.word}
                onChange={(e) => setNewWordForm(prev => ({ ...prev, word: e.target.value }))}
                placeholder="예: apple"
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                뜻 (한국어)
              </label>
              <input
                type="text"
                value={newWordForm.meaning}
                onChange={(e) => setNewWordForm(prev => ({ ...prev, meaning: e.target.value }))}
                placeholder="예: 사과"
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                예문 (선택사항)
              </label>
              <input
                type="text"
                value={newWordForm.example}
                onChange={(e) => setNewWordForm(prev => ({ ...prev, example: e.target.value }))}
                placeholder="예: I eat an apple every morning."
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div className="flex space-x-2">
              <button
                type="submit"
                disabled={!newWordForm.word.trim() || !newWordForm.meaning.trim()}
                className="flex-1 bg-green-600 text-white py-2 rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                추가하기
              </button>
              <button
                type="button"
                onClick={() => setActivePanel(null)}
                className="px-4 py-2 border border-gray-300 text-gray-600 rounded hover:bg-gray-50 transition-colors"
              >
                취소
              </button>
            </div>
          </form>
        </div>
      )}

      {/* 디자인 설정 (개선된 버전) */}
      {activePanel === 'colorTheme' && (
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-gray-800 flex items-center">
              <span className="mr-2">🎨</span>
              디자인 설정
            </h4>
            <button
              onClick={() => setActivePanel(null)}
              className="text-gray-400 hover:text-gray-600 text-xl"
            >
              ×
            </button>
          </div>
          
          <div className="space-y-4">
            {/* AI 디자인 생성 섹션 */}
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-3 rounded-lg border border-purple-200">
              <h5 className="font-medium text-purple-800 mb-2 flex items-center">
                <span className="mr-1">🤖</span>
                AI 디자인 생성
              </h5>
              <div className="space-y-2">
                <textarea
                  value={aiDesignRequest}
                  onChange={(e) => setAiDesignRequest(e.target.value)}
                  placeholder="예: '산뜻한 봄 느낌의 색상으로 바꿔줘', '차분하고 집중할 수 있는 색상 조합'"
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm h-16 resize-none focus:outline-none focus:ring-2 focus:ring-purple-500"
                  disabled={isApplyingDesign}
                />
                <button
                  onClick={handleAIGenerateDesign}
                  disabled={!aiDesignRequest.trim() || isApplyingDesign}
                  className="w-full bg-purple-600 text-white py-2 px-3 rounded hover:bg-purple-700 disabled:opacity-50 text-sm flex items-center justify-center space-x-2"
                >
                  {isApplyingDesign ? (
                    <>
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                      <span>생성 중...</span>
                    </>
                  ) : (
                    <>
                      <span>✨</span>
                      <span>AI로 색상 생성</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* 배경 그라데이션 섹션 */}
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-2">
                배경 그라데이션
              </label>
              <div className="flex space-x-2 mb-2">
                <EnhancedColorPicker
                  label="시작 색상"
                  color={designSettings.background.start}
                  onChange={(color) => handleColorChange('background', 'start', color)}
                />
                <EnhancedColorPicker
                  label="끝 색상"
                  color={designSettings.background.end}
                  onChange={(color) => handleColorChange('background', 'end', color)}
                />
              </div>
              <div 
                className="h-8 rounded border border-gray-300"
                style={{
                  background: `linear-gradient(135deg, ${designSettings.background.start}, ${designSettings.background.end})`
                }}
              ></div>
            </div>
            
            {/* 카드 그라데이션 섹션 */}
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-2">
                카드 그라데이션
              </label>
              <div className="flex space-x-2 mb-2">
                <EnhancedColorPicker
                  label="시작 색상"
                  color={designSettings.card.start}
                  onChange={(color) => handleColorChange('card', 'start', color)}
                />
                <EnhancedColorPicker
                  label="끝 색상"
                  color={designSettings.card.end}
                  onChange={(color) => handleColorChange('card', 'end', color)}
                />
              </div>
              <div 
                className="h-8 rounded border border-gray-300"
                style={{
                  background: `linear-gradient(135deg, ${designSettings.card.start}, ${designSettings.card.end})`
                }}
              ></div>
            </div>
            
            {/* 폰트 색상 섹션 */}
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-2">
                폰트 색상
              </label>
              <div className="flex space-x-2">
                <EnhancedColorPicker
                  label="주 색상"
                  color={designSettings.font.primary}
                  onChange={(color) => handleColorChange('font', 'primary', color)}
                />
                <EnhancedColorPicker
                  label="보조 색상"
                  color={designSettings.font.secondary}
                  onChange={(color) => handleColorChange('font', 'secondary', color)}
                />
              </div>
            </div>
            
            {/* 적용 버튼 */}
            <div className="pt-2">
              <button
                onClick={handleApplyDesign}
                className="w-full bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center space-x-2 font-medium"
              >
                <span>🎯</span>
                <span>디자인 적용하기</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 현재 단어 목록 */}
      {activePanel === 'wordList' && (
        <WordListEditor 
          wordsList={wordsList}
          onWordUpdate={onWordUpdate}
          onWordDelete={onWordDelete}
          onClose={() => setActivePanel(null)}
        />
      )}
    </div>
  );
};

// ColorPicker 컴포넌트 (기존)
const ColorPicker = ({ label, defaultColor, onChange }) => {
  const [color, setColor] = useState(defaultColor);

  const handleColorChange = (e) => {
    const newColor = e.target.value;
    setColor(newColor);
    onChange(newColor);
  };

  return (
    <div className="flex-1">
      <div className="flex items-center space-x-2">
        <input
          type="color"
          value={color}
          onChange={handleColorChange}
          className="w-8 h-8 border border-gray-300 rounded cursor-pointer"
        />
        <span className="text-xs text-gray-500">{color}</span>
      </div>
      <div className="text-xs text-gray-400 mt-1">{label}</div>
    </div>
  );
};

// EnhancedColorPicker 컴포넌트 (개선된 버전)
const EnhancedColorPicker = ({ label, color, onChange }) => {
  const handleColorChange = (e) => {
    const newColor = e.target.value;
    onChange(newColor);
  };

  return (
    <div className="flex-1">
      <div className="flex items-center space-x-2">
        <input
          type="color"
          value={color}
          onChange={handleColorChange}
          className="w-10 h-10 border border-gray-300 rounded cursor-pointer shadow-sm hover:shadow-md transition-shadow"
        />
        <div className="flex-1">
          <div className="text-xs text-gray-400">{label}</div>
          <div className="text-xs font-mono text-gray-600 bg-gray-50 px-2 py-1 rounded">{color}</div>
        </div>
      </div>
    </div>
  );
};

// WordListEditor 컴포넌트 - 인라인 편집 가능한 단어 목록
const WordListEditor = ({ wordsList, onWordUpdate, onWordDelete, onClose }) => {

  
  // 데이터 구조 정규화 (안전장치)
  const normalizedWordsList = wordsList.map(item => ({
    word: item.word || item.english || '',
    meaning: item.meaning || item.korean || '',
    example: item.example || item.sentence || ''
  }));
  
  const [editingIndex, setEditingIndex] = useState(-1);
  const [editForm, setEditForm] = useState({ word: '', meaning: '', example: '' });

  const handleStartEdit = (index) => {
    setEditingIndex(index);
    const wordData = normalizedWordsList[index];
    setEditForm({
      word: wordData.word,
      meaning: wordData.meaning,
      example: wordData.example
    });
  };

  const handleSaveEdit = () => {
    if (editForm.word.trim() && editForm.meaning.trim()) {
      const updatedData = {
        word: editForm.word.trim(),
        meaning: editForm.meaning.trim(),
        example: editForm.example.trim()
      };
      onWordUpdate(editingIndex, updatedData);
      setEditingIndex(-1);
      setEditForm({ word: '', meaning: '', example: '' });
    }
  };

  const handleCancelEdit = () => {
    setEditingIndex(-1);
    setEditForm({ word: '', meaning: '', example: '' });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSaveEdit();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-4 border-2 border-blue-200">
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-semibold text-gray-800 flex items-center">
          <span className="mr-2">📚</span>
          단어 목록 편집 ({wordsList.length}개)
        </h4>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 text-xl"
        >
          ×
        </button>
      </div>
      
      <div className="max-h-96 overflow-y-auto">
        {console.log('단어 목록 (원본):', wordsList)}
        {console.log('단어 목록 (정규화):', normalizedWordsList)}
        {wordsList.length > 0 ? (
          <div className="space-y-3">
            {normalizedWordsList.map((word, index) => (
              <div key={index} className="border border-gray-200 rounded p-3 hover:bg-gray-50">
                {editingIndex === index ? (
                  // 편집 모드
                  <div className="space-y-2">
                    <div>
                      <input
                        type="text"
                        value={editForm.word}
                        onChange={(e) => setEditForm(prev => ({ ...prev, word: e.target.value }))}
                        placeholder="영어 단어"
                        className="w-full border border-blue-300 rounded px-2 py-1 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500"
                        onKeyDown={handleKeyDown}
                        autoFocus
                      />
                    </div>
                    <div>
                      <input
                        type="text"
                        value={editForm.meaning}
                        onChange={(e) => setEditForm(prev => ({ ...prev, meaning: e.target.value }))}
                        placeholder="한국어 뜻"
                        className="w-full border border-blue-300 rounded px-2 py-1 text-sm text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        onKeyDown={handleKeyDown}
                      />
                    </div>
                    <div>
                      <input
                        type="text"
                        value={editForm.example}
                        onChange={(e) => setEditForm(prev => ({ ...prev, example: e.target.value }))}
                        placeholder="예문 (선택사항)"
                        className="w-full border border-gray-300 rounded px-2 py-1 text-xs text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        onKeyDown={handleKeyDown}
                      />
                    </div>
                    <div className="flex space-x-2 pt-1">
                      <button
                        onClick={handleSaveEdit}
                        disabled={!editForm.word.trim() || !editForm.meaning.trim()}
                        className="px-3 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        ✓ 저장
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="px-3 py-1 bg-gray-500 text-white rounded text-xs hover:bg-gray-600"
                      >
                        ✗ 취소
                      </button>
                    </div>
                  </div>
                ) : (
                  // 표시 모드
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="font-medium text-blue-600 text-sm">{word.word}</div>
                      <div className="text-gray-600 text-sm">{word.meaning}</div>
                      {word.example && (
                        <div className="text-gray-500 text-xs italic mt-1 leading-relaxed">
                          "{word.example}"
                        </div>
                      )}
                    </div>
                    <div className="flex space-x-1 ml-2">
                      <button
                        onClick={() => handleStartEdit(index)}
                        className="px-2 py-1 bg-blue-100 text-blue-600 hover:bg-blue-200 rounded text-xs font-medium transition-colors flex items-center space-x-1"
                        title="편집"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        <span>편집</span>
                      </button>
                      <button
                        onClick={() => onWordDelete(index)}
                        className="px-2 py-1 bg-red-100 text-red-600 hover:bg-red-200 rounded text-xs font-medium transition-colors flex items-center space-x-1"
                        title="삭제"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        <span>삭제</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-gray-500 text-center py-8 bg-gray-50 rounded-lg">
            <div className="text-3xl mb-2">📝</div>
            <p className="mb-3">단어가 없습니다</p>
            <p className="text-sm text-blue-600">
              "AI와 함께 새단어 추가하기" 또는 "직접 단어 추가하기"를 이용해 단어를 추가해주세요
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default InteractivePreview;